import { isSourceCovered, SOURCE_ROW_KEY } from '@/services/sourceResolver.js'

const extractActionsPropertiesByType = (theInsightEntity, actionKey, actionOrActionValue, attributionWindowKeys) => {
    // the insights object has a 'actions' and 'action_values' array that contains all the actions, and each action has a 'action_type' key that is the key we are looking for
    // Both extracted the same but from a different array - 'actions' or 'action_values'
    // Also, for each action we have 'value' and we have also values per attribution window
    let finalValue = 0
    const action = theInsightEntity[actionOrActionValue]?.find?.(action => action.action_type === actionKey)

    // When we have explicit attribution window keys, sum the action's value across those windows.
    // An empty array means "use the campaign default" (the 'Default' attribution switch on the space),
    // so we fall through to the action's top-level `.value` below.
    if (attributionWindowKeys && attributionWindowKeys.length) {
        if (action) {
            for (const key of attributionWindowKeys) {
                finalValue += action[key] ? Number(action[key]) : 0
            }
        }
        return finalValue
    }

    // Default — take the campaign's default attribution value
    finalValue = action?.value
    return finalValue ? Number(finalValue) : 0
}


// Source (TripleWhale / Northbeam) coverage is now computed from each metric's `dependsOn` chain in sourceResolver
// (see isSourceCovered). This map remains only as the account-level fallback used by
// MetricsToolbar when no per-row source data is available. Both providers return the same
// `account` fields, so one map serves them all.
export const SOURCE_ACCOUNT_METRIC_MAP = {
    spend: 'spend',
    purchase_value: 'revenue',
    roas: 'roas',
    purchases: 'purchases',
    cpa: 'cpa',
    cost_per_purchase: 'cpa',
}

export const hasSourceEquivalent = (metricKey, provider) => {
    return isSourceCovered(metricKey, provider)
}

export const getSourceValue = (row, metricKey, provider) => row?.[SOURCE_ROW_KEY[provider]]?.[metricKey]


// a 'website' metric means the user's website! the opposite of what meta use. So the user website will be a metric with 'offsite_' by meta. because it's off the facebook website hence it's offsite.
export const AllMetrics = [
    {
        "key": "spend",
        "name": "Spend",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": true,
        "percentages": false,
        "description": "Total amount of money spent on the asset.",
        "formula": "Sum of all expenses related to the asset.",
        "bestValue": "high",
        "isNatural": true,
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity) => theInsightEntity.spend ? Number(theInsightEntity.spend) : 0,
            tiktok_ads: (theInsightEntity) => theInsightEntity['spend'] ? Number(theInsightEntity['spend']) : 0,
            google_ads: (theInsightEntity) => theInsightEntity['spend'] ? Number(theInsightEntity['spend']) : 0,
            axon: (theInsightEntity) => theInsightEntity['spend'] ? Number(theInsightEntity['spend']) : 0, // this is 'cost' on axon but we normalize it on the server already for other purposes
            linkedin_ads: (theInsightEntity) => theInsightEntity['spend'] ? Number(theInsightEntity['spend']) : 0,
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.spend ? theTotalEntity.spend + theCurrentEntity.spend : theCurrentEntity.spend
        },
        // I am not sure if it should be per metric, might change in the future but for now the generic 'normalizeMetric' is enough
        // normalizeFunction: (value) => {}
    },
    {
        "key": "purchase_value",
        "name": "Purchase value",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": true,
        "percentages": false,
        "description": "Total value from purchases.",
        "formula": "Sum of Purchase Values",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'purchase', 'action_values', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => {
                // console.log(theInsightEntity['total_purchase_value'])
                return theInsightEntity['total_complete_payment_rate'] ? Number(theInsightEntity['total_complete_payment_rate']) : 0
            },
            google_ads: (theInsightEntity) => theInsightEntity['conversions_value'] ? Number(theInsightEntity['conversions_value']) : 0,
            axon: (theInsightEntity) => {
                return theInsightEntity['total_rev'] ? Number(theInsightEntity['total_rev'])
                    : theInsightEntity['iap_rev'] ? Number(theInsightEntity['iap_rev'])
                        : theInsightEntity['ad_rev'] ? Number(theInsightEntity['ad_rev'])
                            : 0
            },
            linkedin_ads: (theInsightEntity) => theInsightEntity['conversionValueInLocalCurrency'] ? Number(theInsightEntity['conversionValueInLocalCurrency']) : 0,
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.purchase_value ? theTotalEntity.purchase_value + theCurrentEntity.purchase_value : theCurrentEntity.purchase_value
        },
    },
    {
        "key": "roas",
        "name": "ROAS",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": false,
        "displaySum": false,
        "displayAverage": true,
        "bestValue": "high",
        "description": "Return on ad spend - a ratio of revenue generated for every dollar spent.",
        "formula": "Revenue / Spend",
        "dependsOn": ["spend", "purchase_value"],
        // Also option to check the 'purchase_roas' array under the 'omni_purchase' action_type
        // extractors: {
        //     tiktok_ads: (theInsightEntity) => theInsightEntity['roas'] ? Number(theInsightEntity['roas']) : 0
        // },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.purchase_value && theTotalEntity.spend ? theTotalEntity.purchase_value / theTotalEntity.spend : 0
        },
    },
    {
        "key": "purchases",
        "name": "Purchases",
        "color": "bg-gradient-to-r from-teal-400 to-green-500",
        "fromColor": "#2DD4BF",
        "toColor": "#22C55E",
        "currency": false,
        "percentages": false,
        "description": "Total number of purchase events",
        "formula": "Sum of Purchase Events",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'purchase', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['complete_payment'] ? Number(theInsightEntity['complete_payment']) : 0,
            google_ads: (theInsightEntity) => theInsightEntity['conversions'] ? Number(theInsightEntity['conversions']) : 0,
            // the _7d is for the last 7 days. we could use other options like _1d, _30d, _90d, etc.
            axon: (theInsightEntity) => theInsightEntity['sales'] ? Number(theInsightEntity['sales']) : 0,
            linkedin_ads: (theInsightEntity) => theInsightEntity['externalWebsiteConversions'] ? Number(theInsightEntity['externalWebsiteConversions']) : 0,
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.purchases ? theTotalEntity.purchases + theCurrentEntity.purchases : theCurrentEntity.purchases
        },
    },
    {
        "key": "cpa",
        "name": "CPA",
        "color": "bg-gradient-to-r from-indigo-500 to-purple-500",
        "fromColor": "#6366F1",
        "toColor": "#A855F7",
        "currency": true,
        "percentages": false,
        "description": "Cost per acquisition - the cost to acquire a customer or conversion.",
        "formula": "SPEND / PURCHASES",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["spend", "purchases"],
        // extractors: {
        //     tiktok_ads: (theInsightEntity) => theInsightEntity['cpa'] ? Number(theInsightEntity['cpa']) : 0
        // },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.spend && theTotalEntity.purchases ? (theTotalEntity.spend / theTotalEntity.purchases) : 0
        },
    },
    {
        "key": "cost_per_purchase",
        "name": "Cost per purchase",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": true,
        "percentages": false,
        "description": "Cost per purchase - the cost to acquire a customer or conversion.",
        "formula": "Total Spend / Total Purchases",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["spend", "purchases"],
        // extractors: {
        //     tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_purchase'] ? Number(theInsightEntity['cost_per_purchase']) : 0
        // },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.spend && theTotalEntity.purchases ? (theTotalEntity.spend / theTotalEntity.purchases) : 0
        },
    },
    {
        "key": "aov",
        "name": "AOV",
        "color": "bg-gradient-to-r from-teal-400 to-green-500",
        "fromColor": "#2DD4BF",
        "toColor": "#22C55E",
        "currency": true,
        "percentages": false,
        "description": "Average order value - the average amount spent per order.",
        "formula": "Total Revenue / Total Orders",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["purchase_value", "purchases"], // purchase_value,
        // extractors: {
        // tiktok_ads: (theInsightEntity) => theInsightEntity['aov'] ? Number(theInsightEntity['aov']) : 0
        // },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.purchase_value && theTotalEntity.purchases ? (theTotalEntity.purchase_value / theTotalEntity.purchases) : 0
        },
    },
    {
        "key": "website_purchase_count",
        "name": "Website purchase count",
        "color": "bg-gradient-to-r from-teal-400 to-green-500",
        "fromColor": "#2DD4BF",
        "toColor": "#22C55E",
        "currency": false,
        "percentages": false,
        "description": "Total number of purchases made through the website.",
        "formula": "Sum of Website Purchases",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'offsite_conversion.fb_pixel_purchase', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['complete_payment'] ? Number(theInsightEntity['complete_payment']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.website_purchase_count ? theTotalEntity.website_purchase_count + theCurrentEntity.website_purchase_count : theCurrentEntity.website_purchase_count
        },
    },
    {
        "key": "website_purchase_value",
        "name": "Website purchase value",
        "color": "bg-gradient-to-r from-pink-500 to-yellow-500",
        "fromColor": "#EC4899",
        "toColor": "#EAB308",
        "currency": true,
        "percentages": false,
        "description": "Total value generated from website purchases.",
        "formula": "Sum of Website Purchase Values",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'offsite_conversion.fb_pixel_purchase', 'action_values', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['total_complete_payment_rate'] ? Number(theInsightEntity['total_complete_payment_rate']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.website_purchase_value ? theTotalEntity.website_purchase_value + theCurrentEntity.website_purchase_value : theCurrentEntity.website_purchase_value
        },

    },
    {
        "key": "aov_website",
        "name": "AOV (website)",
        "color": "bg-gradient-to-r from-teal-400 to-green-500",
        "fromColor": "#2DD4BF",
        "toColor": "#22C55E",
        "currency": true,
        "percentages": false,
        "description": "Average order value on the website.",
        "formula": "Total Website Revenue / Total Website Orders",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["website_purchase_value", "website_purchase_count"],
        // extractors: {
        // tiktok_ads: (theInsightEntity) => theInsightEntity['aov_website'] ? Number(theInsightEntity['aov_website']) : 0
        // },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.website_purchase_value && theTotalEntity.website_purchase_count ? (theTotalEntity.website_purchase_value / theTotalEntity.website_purchase_count) : 0
        },
    },
    {
        "key": "shops_transactions",
        "name": "Shops Transactions",
        "color": "bg-gradient-to-r from-teal-400 to-green-500",
        "fromColor": "#2DD4BF",
        "toColor": "#22C55E",
        "currency": false,
        "percentages": false,
        "description": "Total transactions made in shops.",
        "formula": "Sum of Shop Transactions",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'onsite_web_purchase', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['shops_transactions'] ? Number(theInsightEntity['shops_transactions']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.shops_transactions ? theTotalEntity.shops_transactions + theCurrentEntity.shops_transactions : theCurrentEntity.shops_transactions
        },
    },
    {
        "key": "shops_revenue",
        "name": "Shops Revenue",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": true,
        "percentages": false,
        "description": "Total revenue generated from shops.",
        "formula": "Sum of Revenue from Shops",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'onsite_web_purchase', 'action_values', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['shops_revenue'] ? Number(theInsightEntity['shops_revenue']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.shops_revenue ? theTotalEntity.shops_revenue + theCurrentEntity.shops_revenue : theCurrentEntity.shops_revenue
        },
    },
    {
        "key": "reach",
        "name": "Reach",
        "color": "bg-gradient-to-r from-teal-400 to-green-500",
        "fromColor": "#2DD4BF",
        "toColor": "#22C55E",
        "currency": false,
        "percentages": false,
        "description": "Total number of unique people who saw the ad.",
        "formula": "",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity) => theInsightEntity.reach ? Number(theInsightEntity.reach) : 0,
            tiktok_ads: (theInsightEntity) => theInsightEntity['reach'] ? Number(theInsightEntity['reach']) : 0
        },
        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.reach ? theTotalEntity.reach + theCurrentEntity.reach : theCurrentEntity.reach
        },
    },
    {
        "key": "frequency",
        "name": "Frequency",
        "color": "bg-gradient-to-r from-teal-400 to-green-500",
        "fromColor": "#2DD4BF",
        "toColor": "#22C55E",
        "currency": false,
        "percentages": false,
        "description": "How many times, on average, each person saw the ad during the selected date range.",
        "formula": "",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "shouldDivideByAdsLength": true,
        extractors: {
            meta: (theInsightEntity) => theInsightEntity.frequency ? Number(theInsightEntity.frequency) : 0,
            tiktok_ads: (theInsightEntity) => theInsightEntity['frequency'] ? Number(theInsightEntity['frequency']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.frequency ? theTotalEntity.frequency + theCurrentEntity.frequency : theCurrentEntity.frequency
        },
    },
    {
        "key": "results_data", // This MUST have different key than the 'results' so we won't override the 'results' key
        "name": "Results",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": false,
        "percentages": false,
        "description": "The 'Results' defined in the ad set",
        "formula": "Sum of Results",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                const resultsValuesByAttributionWindow = theInsightEntity.results?.[0]?.values
                let finalValue = 0
                if (resultsValuesByAttributionWindow && attributionWindowKeys) {
                    for (const valuePerWindow of resultsValuesByAttributionWindow) {
                        if (valuePerWindow?.attribution_windows?.find(attributionWindow => attributionWindow === 'default')) {
                            finalValue += valuePerWindow.value ? Number(valuePerWindow.value) : 0
                        }
                    }
                }

                if (!finalValue) {
                    const firstValueWeCanGet = theInsightEntity.results?.[0]?.values?.[0]?.value
                    if (firstValueWeCanGet) {
                        finalValue = firstValueWeCanGet ? Number(firstValueWeCanGet) : 0
                    }
                }
                // if (resultsValuesByAttributionWindow && attributionWindowKeys) {
                //     for (const valuePerWindow of resultsValuesByAttributionWindow) {
                //         for (const key of attributionWindowKeys) {
                //             if (valuePerWindow?.attribution_windows?.find(attributionWindow => attributionWindow === key)) {
                //                 finalValue += valuePerWindow.value ? Number(valuePerWindow.value) : 0
                //             }
                //         }
                //     }
                // }

                return finalValue
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['conversion'] ? Number(theInsightEntity['conversion']) : 0,
            google_ads: (theInsightEntity) => theInsightEntity['conversions'] ? Number(theInsightEntity['conversions']) : 0,
            // LinkedIn doesn't have a generic "results" concept — surface the same conversion count as purchases.
            linkedin_ads: (theInsightEntity) => theInsightEntity['externalWebsiteConversions'] ? Number(theInsightEntity['externalWebsiteConversions']) : 0,
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.results_data ? theTotalEntity.results_data + theCurrentEntity.results_data : theCurrentEntity.results_data
        }
    },
    {
        "key": "cost_per_results_data", // This MUST have different key than the 'results' so we won't override the 'results' key
        "name": "Cost per result",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": true,
        "percentages": false,
        "description": "Cost per result",
        "formula": "Total Spend / Total Results",
        "bestValue": "low",
        "displaySum": true,
        "displayAverage": true,
        "dependsOn": ["spend"],
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                const costPerResultValuesByAttributionWindow = theInsightEntity.cost_per_result?.[0]?.values
                let finalValue = 0
                if (costPerResultValuesByAttributionWindow) {
                    for (const valuePerWindow of costPerResultValuesByAttributionWindow) {
                        if (valuePerWindow?.attribution_windows?.find(attributionWindow => attributionWindow === 'default')) {
                            finalValue += valuePerWindow.value ? Number(valuePerWindow.value) : 0
                        }
                    }
                }

                if (!finalValue) {
                    const firstValueWeCanGet = theInsightEntity.cost_per_result?.[0]?.values?.[0]?.value
                    if (firstValueWeCanGet) {
                        finalValue = firstValueWeCanGet ? Number(firstValueWeCanGet) : 0
                    }
                }

                // if (resultsValuesByAttributionWindow && attributionWindowKeys) {
                //     for (const valuePerWindow of resultsValuesByAttributionWindow) {
                //         for (const key of attributionWindowKeys) {
                //             if (valuePerWindow?.attribution_windows?.find(attributionWindow => attributionWindow === key)) {
                //                 finalValue += valuePerWindow.value ? Number(valuePerWindow.value) : 0
                //             }
                //         }
                //     }
                // }

                return finalValue
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_conversion'] ? Number(theInsightEntity['cost_per_conversion']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            // This is for the actual value from the API
            return theTotalEntity.cost_per_results_data ? theTotalEntity.cost_per_results_data + theCurrentEntity.cost_per_results_data : theCurrentEntity.cost_per_results_data
            // This is for the calculated value
            // return theTotalEntity.results_data && theCurrentEntity.spend ? (theTotalEntity.spend / theTotalEntity.results_data) : theCurrentEntity.cost_per_results_data
        }
    },
    {
        "key": "aov_shop",
        "name": "AOV (shop)",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": true,
        "percentages": false,
        "description": "Average order value in the Shop. (Instagram Shops / Facebook Shops)",
        "formula": "Total Shop Revenue / Total Shop Orders",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["shops_transactions", "shops_revenue"],
        // extractors: {
        // tiktok_ads: (theInsightEntity) => theInsightEntity['aov_shop'] ? Number(theInsightEntity['aov_shop']) : 0
        // },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.shops_revenue && theTotalEntity.shops_transactions ? (theTotalEntity.shops_revenue / theTotalEntity.shops_transactions) : 0
        },
    },
    {
        "key": "impressions",
        "name": "Impressions",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": false,
        "description": "Total number of times the ad was displayed.",
        "formula": "Count of ad views",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity) => theInsightEntity.impressions ? Number(theInsightEntity.impressions) : 0,
            tiktok_ads: (theInsightEntity) => theInsightEntity['impressions'] ? Number(theInsightEntity['impressions']) : 0,
            google_ads: (theInsightEntity) => theInsightEntity['impressions'] ? Number(theInsightEntity['impressions']) : 0,
            axon: (theInsightEntity) => theInsightEntity['impressions'] ? Number(theInsightEntity['impressions']) : 0,
            linkedin_ads: (theInsightEntity) => theInsightEntity['impressions'] ? Number(theInsightEntity['impressions']) : 0,
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.impressions ? theTotalEntity.impressions + theCurrentEntity.impressions : theCurrentEntity.impressions
        },
    },
    {
        "key": "clicks_outbound",
        "name": "Clicks (outbound)",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": false,
        "percentages": false,
        "description": "Total number of outbound clicks.",
        "formula": "Sum of all outbound clicks",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                // It should be this - 'outbound_click' don't know why i don't get it.
                return extractActionsPropertiesByType(theInsightEntity, 'link_click', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['clicks'] ? Number(theInsightEntity['clicks']) : 0,
            google_ads: (theInsightEntity) => theInsightEntity['clicks'] ? Number(theInsightEntity['clicks']) : 0,
            axon: (theInsightEntity) => theInsightEntity['clicks'] ? Number(theInsightEntity['clicks']) : 0,
            // LinkedIn landingPageClicks = clicks that took the user off-platform to the creative's landing page.
            linkedin_ads: (theInsightEntity) => theInsightEntity['landingPageClicks'] ? Number(theInsightEntity['landingPageClicks']) : 0,
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.clicks_outbound ? theTotalEntity.clicks_outbound + theCurrentEntity.clicks_outbound : theCurrentEntity.clicks_outbound
        },
    },
    {
        "key": "cpm",
        "name": "CPM",
        "color": "bg-gradient-to-r from-pink-500 to-yellow-500",
        "fromColor": "#EC4899",
        "toColor": "#EAB308",
        "currency": true,
        "percentages": false,
        "description": "Cost per 1000 impressions",
        "formula": "(Total Spend / Total Impressions) * 1000",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["spend", "impressions"],
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['cpm'] ? Number(theInsightEntity['cpm']) : 0
        },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.spend && theTotalEntity.impressions ? (theTotalEntity.spend / theTotalEntity.impressions) * 1000 : 0
            // return theTotalEntity.cpm ? theTotalEntity.cpm + theCurrentEntity.cpm : theCurrentEntity.cpm
        },
    },
    {
        "key": "link_clicks",
        "name": "Link clicks",
        "color": "bg-gradient-to-r from-pink-500 to-yellow-500",
        "fromColor": "#EC4899",
        "toColor": "#EAB308",
        "currency": false,
        "percentages": false,
        "description": "Total number of link clicks.",
        "formula": "Sum of all link clicks",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'link_click', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['link_clicks'] ? Number(theInsightEntity['link_clicks']) : 0,
            google_ads: (theInsightEntity) => theInsightEntity['clicks'] ? Number(theInsightEntity['clicks']) : 0,
            axon: (theInsightEntity) => theInsightEntity['clicks'] ? Number(theInsightEntity['clicks']) : 0,
            linkedin_ads: (theInsightEntity) => theInsightEntity['landingPageClicks'] ? Number(theInsightEntity['landingPageClicks']) : 0,
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.link_clicks ? theTotalEntity.link_clicks + theCurrentEntity.link_clicks : theCurrentEntity.link_clicks
        },
    },
    {
        "key": "company_page_clicks",
        "name": "Clicks to LinkedIn pages",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": false,
        "percentages": false,
        "description": "Clicks that took the user to the advertiser's LinkedIn page.",
        "formula": "Sum of company page clicks",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            linkedin_ads: (theInsightEntity) => theInsightEntity['companyPageClicks'] ? Number(theInsightEntity['companyPageClicks']) : 0,
        },
        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.company_page_clicks ? theTotalEntity.company_page_clicks + theCurrentEntity.company_page_clicks : theCurrentEntity.company_page_clicks
        },
    },
    {
        "key": "click_conversions",
        "name": "Click conversions",
        "color": "bg-gradient-to-r from-pink-500 to-yellow-500",
        "fromColor": "#EC4899",
        "toColor": "#EAB308",
        "currency": false,
        "percentages": false,
        "description": "Conversions attributed to a click on the ad (post-click).",
        "formula": "Sum of post-click conversions",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            linkedin_ads: (theInsightEntity) => theInsightEntity['externalWebsitePostClickConversions'] ? Number(theInsightEntity['externalWebsitePostClickConversions']) : 0,
        },
        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.click_conversions ? theTotalEntity.click_conversions + theCurrentEntity.click_conversions : theCurrentEntity.click_conversions
        },
    },
    {
        "key": "ctr_link_click",
        "name": "CTR (link click)",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": false,
        "percentages": true,
        "description": "Click-through rate for link clicks.",
        "formula": "(Link Clicks / Impressions) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["link_clicks", "impressions"],
        // metaExtractFunction: (theInsightEntity) => {
        //     const ctrLinkClick = theInsightEntity.website_ctr?.find(websiteSingleCtr => websiteSingleCtr.action_type === 'link_click')?.value
        //     return ctrLinkClick ? Number(ctrLinkClick) : 0
        // },
        // extractors: {
        // tiktok_ads: (theInsightEntity) => theInsightEntity['ctr_link_click'] ? Number(theInsightEntity['ctr_link_click']) : 0
        // },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.link_clicks && theTotalEntity.impressions ? (theTotalEntity.link_clicks / theTotalEntity.impressions) * 100 : 0
        },
    },
    {
        "key": "ctr_outbound",
        "name": "CTR (outbound)",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": false,
        "percentages": true,
        "description": "Click-through rate for outbound clicks.",
        "formula": "(Outbound Clicks / Impressions) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["clicks_outbound", "impressions"],
        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.clicks_outbound && theTotalEntity.impressions ? (theTotalEntity.clicks_outbound / theTotalEntity.impressions) * 100 : 0
        },
        // extractors: {
        // tiktok_ads: (theInsightEntity) => theInsightEntity['ctr_outbound'] ? Number(theInsightEntity['ctr_outbound']) : 0
        // },
    },
    {
        "key": "website_purchase_roas",
        "name": "Website purchase ROAS",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": false,
        "description": "Return on ad spend for website purchases.",
        "formula": "Revenue from Website Purchases / Spend",
        "bestValue": "high",
        "dependsOn": ["website_purchase_value", "spend"],
        "displaySum": false,
        "displayAverage": true,
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.website_purchase_value && theTotalEntity.spend ? (theTotalEntity.website_purchase_value / theTotalEntity.spend) : 0
        },
        // extractors: {
        // tiktok_ads: (theInsightEntity) => theInsightEntity['website_purchase_roas'] ? Number(theInsightEntity['website_purchase_roas']) : 0
        // },
    },
    {
        "key": "clicks_all",
        "name": "Clicks (all)",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": false,
        "description": "Total number of clicks (all types).",
        "formula": "Sum of all clicks",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity) => theInsightEntity.clicks ? Number(theInsightEntity.clicks) : 0,
            tiktok_ads: (theInsightEntity) => theInsightEntity['clicks'] ? Number(theInsightEntity['clicks']) : 0,
            google_ads: (theInsightEntity) => theInsightEntity['clicks'] ? Number(theInsightEntity['clicks']) : 0,
            axon: (theInsightEntity) => theInsightEntity['clicks'] ? Number(theInsightEntity['clicks']) : 0,
            linkedin_ads: (theInsightEntity) => theInsightEntity['clicks'] ? Number(theInsightEntity['clicks']) : 0,
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.clicks_all ? theTotalEntity.clicks_all + theCurrentEntity.clicks_all : theCurrentEntity.clicks_all
        },
    },
    {
        "key": "ctr",
        "name": "CTR",
        "color": "bg-gradient-to-r from-indigo-500 to-purple-500",
        "fromColor": "#6366F1",
        "toColor": "#A855F7",
        "currency": false,
        "percentages": true,
        "description": "Click-through rate for all clicks.",
        "formula": "(Total Clicks / Total Impressions) * 100",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "dependsOn": ["clicks_all", "impressions"],
        // extractors: {
        // tiktok_ads: (theInsightEntity) => theInsightEntity['ctr'] ? Number(theInsightEntity['ctr']) : 0
        // },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.clicks_all && theTotalEntity.impressions ? (theTotalEntity.clicks_all / theTotalEntity.impressions) * 100 : 0
        },
    },
    {
        "key": "click_to_purchase",
        "name": "Click to purchase",
        "color": "bg-gradient-to-r from-teal-400 to-green-500",
        "fromColor": "#2DD4BF",
        "toColor": "#22C55E",
        "currency": false,
        "percentages": true,
        "description": "Ratio of clicks that lead to purchases.",
        "formula": "(Purchase Count / Link Clicks) * 100",
        "bestValue": "high",
        "dependsOn": ["clicks_all", "purchases"],
        "displaySum": false,
        "displayAverage": true,
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.click_to_purchase = theTotalEntity.clicks_all && theTotalEntity.purchases ? (theTotalEntity.purchases / theTotalEntity.clicks_all) * 100 : 0
        },
        // extractors: {
        //     tiktok_ads: (theInsightEntity) => theInsightEntity['click_to_purchase'] ? Number(theInsightEntity['click_to_purchase']) : 0
        // },
    },
    {
        "key": "click_to_website_purchase",
        "name": "Click to website purchase",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": true,
        "description": "Click to website purchase ratio, Out of users who clicked, percentage that made a website purchase",
        "formula": "(Offsite Purchase Count / Link Clicks) * 100",
        "bestValue": "high",
        "dependsOn": ["clicks_all", "website_purchase_count"],
        "displaySum": false,
        "displayAverage": true,
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.click_to_website_purchase = theTotalEntity.clicks_all && theTotalEntity.website_purchase_count ? (theTotalEntity.website_purchase_count / theTotalEntity.clicks_all) * 100 : 0
        },
        // extractors: {
        //     tiktok_ads: (theInsightEntity) => theInsightEntity['click_to_website_purchase'] ? Number(theInsightEntity['click_to_website_purchase']) : 0
        // },
    },
    {
        "key": "purchases_per_1000_impressions",
        "name": "Purchases per 1,000 impressions",
        "color": "bg-gradient-to-r from-teal-400 to-green-500",
        "fromColor": "#2DD4BF",
        "toColor": "#22C55E",
        "currency": false,
        "percentages": false,
        "description": "Number of purchases per 1,000 impressions.",
        "formula": "(Purchases / Impressions) * 1000",
        "bestValue": "high",
        "dependsOn": ["purchases", "impressions"],
        "displaySum": false,
        "displayAverage": true,
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.purchases_per_1000_impressions = theTotalEntity.purchases && theTotalEntity.impressions ? (theTotalEntity.purchases / theTotalEntity.impressions) * 1000 : 0
        },
        // extractors: {
        //     tiktok_ads: (theInsightEntity) => theInsightEntity['purchases_per_1000_impressions'] ? Number(theInsightEntity['purchases_per_1000_impressions']) : 0
        // },
    },
    {
        "key": "revenue_per_1000_impressions",
        "name": "Revenue per 1,000 impressions",
        "color": "bg-gradient-to-r from-teal-400 to-green-500",
        "fromColor": "#2DD4BF",
        "toColor": "#22C55E",
        "currency": true,
        "percentages": false,
        "description": "Revenue generated per 1,000 impressions.",
        "formula": "(PURCHASE VALUE / IMPRESSIONS) * 1000",
        "bestValue": "high",
        "dependsOn": ["purchase_value", "impressions"],
        "displaySum": false,
        "displayAverage": true,
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.revenue_per_1000_impressions = theTotalEntity.purchase_value && theTotalEntity.impressions ? (theTotalEntity.purchase_value / theTotalEntity.impressions) * 1000 : 0
        },
        // extractors: {
        //     tiktok_ads: (theInsightEntity) => theInsightEntity['revenue_per_1000_impressions'] ? Number(theInsightEntity['revenue_per_1000_impressions']) : 0
        // },
    },
    {
        "key": "cpc_all",
        "name": "CPC (all)",
        "color": "bg-gradient-to-r from-pink-500 to-yellow-500",
        "fromColor": "#EC4899",
        "toColor": "#EAB308",
        "currency": true,
        "percentages": false,
        "description": "Cost per click for all clicks.",
        "formula": "Spend / Total Clicks",
        "bestValue": "low",
        "displaySum": true,
        "displayAverage": true,
        "dependsOn": ["spend", "clicks_all"],

        // extractors: {
        //     tiktok_ads: (theInsightEntity) => theInsightEntity['cpc'] ? Number(theInsightEntity['cpc']) : 0
        // },
        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cpc_all = theTotalEntity.spend && theTotalEntity.clicks_all ? (theTotalEntity.spend / theTotalEntity.clicks_all) : 0
            // return theTotalEntity.spend ? theTotalEntity.clicks_all + theCurrentEntity.cpc_all : theCurrentEntity.cpc_all
        },
    },

    {
        "key": "cpc_link_click",
        "name": "CPC (link click)",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": true,
        "percentages": false,
        "description": "Cost per link click.",
        "formula": "Spend / Link Clicks",
        "bestValue": "low",
        "dependsOn": ["spend", "link_clicks"],
        "displaySum": false,
        "displayAverage": true,
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.spend && theTotalEntity.link_clicks ? theTotalEntity.spend / theTotalEntity.link_clicks : 0
        },
        // extractors: {
        //     tiktok_ads: (theInsightEntity) => theInsightEntity['cpc_link_click'] ? Number(theInsightEntity['cpc_link_click']) : 0
        // },
    },
    {
        "key": "cpc_outbound_link_click",
        "name": "CPC (outbound link click)",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "currency": true,
        "percentages": false,
        "description": "Cost per outbound link click.",
        "formula": "Spend / Outbound Clicks",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        // extractors: {
        //     tiktok_ads: (theInsightEntity) => theInsightEntity['cpc_outbound_link_click'] ? Number(theInsightEntity['cpc_outbound_link_click']) : 0
        // },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.spend && theTotalEntity.clicks_outbound ? theTotalEntity.spend / theTotalEntity.clicks_outbound : 0
        },
    },
    {
        "key": "page_likes",
        "name": "Page Likes",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": false,
        "percentages": false,
        "description": "Total number of page likes.",
        "formula": "Sum of all page likes attributed to this creative",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'like', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['page_likes'] ? Number(theInsightEntity['page_likes']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.page_likes ? theTotalEntity.page_likes + theCurrentEntity.page_likes : theCurrentEntity.page_likes
        },
    },
    {
        "key": "percent_likes",
        "name": "% likes",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": false,
        "percentages": true,
        "description": "Percentage of page likes out of total actions.",
        "formula": "(LIKES / IMPRESSIONS) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["page_likes", "impressions"],
        "needVerify": true,
        // extractors: {
        //     tiktok_ads: (theInsightEntity) => theInsightEntity['percent_likes'] ? Number(theInsightEntity['percent_likes']) : 0
        // },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.page_likes && theTotalEntity.impressions ? (theTotalEntity.page_likes / theTotalEntity.impressions) * 100 : 0
        },
    },
    {
        "key": "percent_likes_per_1000",
        "name": "% likes per 1000",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": false,
        "percentages": true,
        "description": "Percentage of page likes per 1,000 actions.",
        "formula": "(LIKES / (IMPRESSIONS / 1000)) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["page_likes", "impressions"],
        "needVerify": true,
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['percent_likes_per_1000'] ? Number(theInsightEntity['percent_likes_per_1000']) : 0
        },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.page_likes && theTotalEntity.impressions ? (theTotalEntity.page_likes / (theTotalEntity.impressions / 1000)) * 100 : 0
        },
    },
    {
        "key": "cost_per_page_like",
        "name": "Cost per page like",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": true,
        "percentages": false,
        "description": "Average cost to acquire a page like.",
        "formula": "Spend / Total Page Likes",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'like', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_page_like'] ? Number(theInsightEntity['cost_per_page_like']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_page_like ? theTotalEntity.cost_per_page_like + theCurrentEntity.cost_per_page_like : theCurrentEntity.cost_per_page_like
        },
    },
    {
        "key": "messaging_conversation_started",
        "name": "Messaging Conversation Started",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": false,
        "percentages": false,
        "description": "Total number of messaging conversation started.",
        "formula": "Messaging conversation started",
        "bestValue": "low",
        "displaySum": true,
        "displayAverage": true,
        // "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                const normal = extractActionsPropertiesByType(theInsightEntity, 'onsite_conversion.messaging_conversation_started', 'actions', attributionWindowKeys)
                if (normal) {
                    return normal
                }
                return extractActionsPropertiesByType(theInsightEntity, 'onsite_conversion.messaging_conversation_started_7d', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['messaging_conversation_started'] ? Number(theInsightEntity['messaging_conversation_started']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.messaging_conversation_started ? theTotalEntity.messaging_conversation_started + theCurrentEntity.messaging_conversation_started : theCurrentEntity.messaging_conversation_started
        },
    },
    {
        "key": "cost_per_messaging_conversation_started",
        "name": "Cost per Messaging Conversation Started",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": false,
        "percentages": false,
        "description": "Average cost to acquire a messaging conversation started.",
        "formula": "Spend / Messaging conversation started",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        // "needVerify": true,
        "dependsOn": ["spend", "messaging_conversation_started"],
        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.spend && theTotalEntity.messaging_conversation_started ? (theTotalEntity.spend / theTotalEntity.messaging_conversation_started) : 0
        },
    },
    {
        "key": "comments",
        "name": "Comments",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": false,
        "description": "Total number of comments on posts.",
        "formula": "Sum of all comments",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'comment', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['comments'] ? Number(theInsightEntity['comments']) : 0,
            linkedin_ads: (theInsightEntity) => theInsightEntity['comments'] ? Number(theInsightEntity['comments']) : 0,
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.comments ? theTotalEntity.comments + theCurrentEntity.comments : theCurrentEntity.comments
        }
    },
    {
        "key": "percent_comments",
        "name": "% comments",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": false,
        "percentages": true,
        "description": "Percentage of total actions that are comments.",
        "formula": "(Comments / IMPRESSIONS) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["comments", "impressions"],
        "needVerify": true,
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.comments && theTotalEntity.impressions ? (theTotalEntity.comments / theTotalEntity.impressions) * 100 : 0
        },
    },
    {
        "key": "percent_comments_per_1000",
        "name": "% comments per 1000 impressions",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": false,
        "percentages": true,
        "description": "Percentage of comments per 1,000 actions.",
        "formula": "(Comments / Total Actions) * 1000",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["comments", "impressions"],
        "needVerify": true,
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.comments && theTotalEntity.impressions ? (theTotalEntity.comments / (theTotalEntity.impressions / 1000)) * 100 : 0
        },
    },
    {
        "key": "shares",
        "name": "Shares",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": false,
        "percentages": false,
        "description": "Total number of shares of post",
        "formula": "Sum of all shares",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'post', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['shares'] ? Number(theInsightEntity['shares']) : 0,
            linkedin_ads: (theInsightEntity) => theInsightEntity['shares'] ? Number(theInsightEntity['shares']) : 0,
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.shares ? theTotalEntity.shares + theCurrentEntity.shares : theCurrentEntity.shares
        }
    },
    {
        "key": "post_share_ratio_psr",
        "name": "Post share ratio (PSR)",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": false,
        "percentages": false,
        "description": "Ratio of shares generated per 1,000 impressions",
        "formula": "(SHARES / IMPRESSIONS) * 1000",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["shares", "impressions"],
        "needVerify": true,
        // extractors: {
        //     tiktok_ads: (theInsightEntity) => theInsightEntity['post_share_ratio_psr'] ? Number(theInsightEntity['post_share_ratio_psr']) : 0
        // },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.shares && theTotalEntity.impressions ? (theTotalEntity.shares / theTotalEntity.impressions) * 1000 : 0
        },
    },
    {
        "key": "reactions",
        "name": "Reactions",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": false,
        "description": "Total number of reactions to posts.",
        "formula": "Sum of all reactions",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'post_reaction', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['reactions'] ? Number(theInsightEntity['reactions']) : 0,
            linkedin_ads: (theInsightEntity) => theInsightEntity['reactions'] ? Number(theInsightEntity['reactions']) : 0,
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.reactions ? theTotalEntity.reactions + theCurrentEntity.reactions : theCurrentEntity.reactions
        }
    },
    {
        "key": "engagement_1_day_click",
        "name": "Engagement (1-day click)",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": false,
        "percentages": false,
        "description": "Number of ad engagements (comments, likes, etc) on a 1-day click attribution window",
        "formula": "Count of 1-day click engagements",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity) => {
                return theInsightEntity.inline_post_engagement ? Number(theInsightEntity.inline_post_engagement) : 0
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['engagement_1_day_click'] ? Number(theInsightEntity['engagement_1_day_click']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.engagement_1_day_click ? theTotalEntity.engagement_1_day_click + theCurrentEntity.engagement_1_day_click : theCurrentEntity.engagement_1_day_click
        }
    },
    {
        "key": "post_engagement",
        "name": "Post Engagement",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": false,
        "percentages": false,
        "description": "Total number of engagements on posts.",
        "formula": "Sum of all post engagements",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'post_engagement', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['post_engagement'] ? Number(theInsightEntity['post_engagement']) : 0,
            linkedin_ads: (theInsightEntity) => theInsightEntity['totalEngagements'] ? Number(theInsightEntity['totalEngagements']) : 0,
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.post_engagement ? theTotalEntity.post_engagement + theCurrentEntity.post_engagement : theCurrentEntity.post_engagement
        }
    },
    {
        "key": "percent_engagements",
        "name": "% engagements",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": false,
        "percentages": true,
        "description": "Percentage of total actions that are engagements.",
        "formula": "(POST ENGAGEMENT / IMPRESSIONS) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["post_engagement", "impressions"],
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['percent_engagements'] ? Number(theInsightEntity['percent_engagements']) : 0
        },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.post_engagement && theTotalEntity.impressions ? (theTotalEntity.post_engagement / theTotalEntity.impressions) * 100 : 0
        },
    },
    {
        "key": "cost_per_engagement",
        "name": "Cost per engagement",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": true,
        "percentages": false,
        "description": "Average cost per engagement.",
        "formula": "SPEND / POST ENGAGEMENTS",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'post_engagement', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_engagement'] ? Number(theInsightEntity['cost_per_engagement']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_engagement ? theTotalEntity.cost_per_engagement + theCurrentEntity.cost_per_engagement : theCurrentEntity.cost_per_engagement
        },
    },
    {
        "key": "see_more_rate",
        "name": "See more rate",
        "color": "bg-gradient-to-r from-pink-500 to-yellow-500",
        "fromColor": "#EC4899",
        "toColor": "#EAB308",
        "currency": false,
        "percentages": true,
        "description": "Rate of users clicking to see more.",
        "formula": "((Clicks (All) - Link Clicks - Post Reactions - Post Comments - Post Shares) / Impressions) * 100",
        "bestValue": "high",
        "dependsOn": ["clicks_all", "link_clicks", "reactions", "comments", "shares", "impressions"],
        "displaySum": false,
        "displayAverage": true,
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['see_more_rate'] ? Number(theInsightEntity['see_more_rate']) : 0
        },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.impressions ? ((theTotalEntity.clicks_all - theTotalEntity.link_clicks - theTotalEntity.reactions - theTotalEntity.comments - theTotalEntity.shares) / theTotalEntity.impressions) * 100 : 0
        },
    },
    {
        "key": "video_avg_time_watched",
        "name": "Video avg. play time",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": false,
        "time": "s",
        "description": "Average play time of video content.",
        "formula": "Total Play Time / Video Views",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity) => {
                const videoAvgTimeWatchBySeconds = theInsightEntity.video_avg_time_watched_actions?.find(action => action.action_type === 'video_view')?.value
                return videoAvgTimeWatchBySeconds ? Number(videoAvgTimeWatchBySeconds) : 0
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['average_video_play'] ? Number(theInsightEntity['average_video_play']) : 0,
            // LinkedIn returns averageVideoWatchTime in MILLISECONDS; this column is in seconds.
            linkedin_ads: (theInsightEntity) => theInsightEntity['averageVideoWatchTime'] ? Number(theInsightEntity['averageVideoWatchTime']) / 1000 : 0,
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.video_avg_time_watched ? theTotalEntity.video_avg_time_watched + theCurrentEntity.video_avg_time_watched : theCurrentEntity.video_avg_time_watched
        }
    },
    {
        "key": "video_watch_time_total",
        "name": "Total video watch time",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": false,
        "time": "s",
        "description": "Total time users spent watching the video ad.",
        "formula": "Sum of video watch time",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            // LinkedIn returns videoWatchTime in MILLISECONDS; this column is in seconds.
            linkedin_ads: (theInsightEntity) => theInsightEntity['videoWatchTime'] ? Number(theInsightEntity['videoWatchTime']) / 1000 : 0,
        },
        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.video_watch_time_total ? theTotalEntity.video_watch_time_total + theCurrentEntity.video_watch_time_total : theCurrentEntity.video_watch_time_total
        },
    },
    {
        "key": "thruplay",
        "name": "Thruplay",
        "color": "bg-gradient-to-r from-pink-500 to-yellow-500",
        "fromColor": "#EC4899",
        "toColor": "#EAB308",
        "currency": false,
        "percentages": false,
        "description": "Total number of times a video was played to completion.",
        "formula": "Sum of completed plays",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'video_view', 'video_thruplay_watched_actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['video_views_p100'] ? Number(theInsightEntity['video_views_p100']) : 0,
            // Google's video_views counts views of 30s+ or completion — closest to thruplay
            google_ads: (theInsightEntity) => theInsightEntity['video_views'] ? Number(theInsightEntity['video_views']) : 0,
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.thruplay ? theTotalEntity.thruplay + theCurrentEntity.thruplay : theCurrentEntity.thruplay
        }
    },

    {
        "key": "3s_video_plays",
        "name": "3s video plays",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": false,
        "percentages": false,
        "description": "The number of times your video was played for at least 3 seconds.",
        "formula": "Count of video plays that lasted 3 seconds or longer",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'video_view', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['video_watched_2s'] ? Number(theInsightEntity['video_watched_2s']) : 0,
            // Google doesn't have a 3s-specific metric — video_views (30s+ or completion) is the closest available
            google_ads: (theInsightEntity) => theInsightEntity['video_views'] ? Number(theInsightEntity['video_views']) : 0,
            // LinkedIn videoViews = "2-second 50% in-view OR CTA click" — closest analogue to Meta's 3-sec view.
            linkedin_ads: (theInsightEntity) => theInsightEntity['videoViews'] ? Number(theInsightEntity['videoViews']) : 0,
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity['3s_video_plays'] ? theTotalEntity['3s_video_plays'] + theCurrentEntity['3s_video_plays'] : theCurrentEntity['3s_video_plays']
        }
    },
    {
        "key": "hook_rate",
        "name": "Hook Rate",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": false,
        "percentages": true,
        "description": "Percentage watching at least 3 seconds",
        "formula": "Hook rate (%) = (3-second video plays ÷ impressions) × 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["impressions", "3s_video_plays"],
        extractors: {
            tiktok_ads: (theInsightEntity) => {
                const videoWatched2s = Number(theInsightEntity['video_watched_2s'] || 0)
                const impressions = Number(theInsightEntity['impressions'] || 0)
                return impressions > 0 ? (videoWatched2s / impressions) * 100 : 0
            },
            // For Google, hook_rate = video_views / impressions (video_views = 30s+ or completion)
            google_ads: (theInsightEntity) => {
                const views = Number(theInsightEntity['video_views'] || 0)
                const imps = Number(theInsightEntity['impressions'] || 0)
                return imps > 0 ? (views / imps) * 100 : 0
            },
        },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity['3s_video_plays'] && theTotalEntity.impressions ? (theTotalEntity['3s_video_plays'] / theTotalEntity.impressions) * 100 : 0
        },

    },
    {
        "key": "hold_rate",
        "name": "Hold Rate",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": true,
        "description": "Percentage of users who held the video to completion.",
        "formula": "(Thruplay / Impressions) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["video_plays", "impressions"],
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['hold_rate'] ? Number(theInsightEntity['hold_rate']) : 0
        },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.video_plays && theTotalEntity.impressions ? (theTotalEntity.video_plays / theTotalEntity.impressions) * 100 : 0
        },
    },
    {
        "key": "15s_per_3s_video_retention",
        "name": "15s/3s video retention",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": true,
        "description": "Retention rate of users after watching 3 seconds of a 15-second video.",
        "formula": "(15s Views - 'THRUPLAY' / 3s Views) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["thruplay", "3s_video_plays"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.thruplay && theTotalEntity['3s_video_plays'] ? (theTotalEntity.thruplay / theTotalEntity['3s_video_plays']) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => {
                const videoViewsP100 = Number(theInsightEntity['video_views_p100'] || 0)
                const videoWatched2s = Number(theInsightEntity['video_watched_2s'] || 0)
                return videoWatched2s > 0 ? (videoViewsP100 / videoWatched2s) * 100 : 0
            }
        },
    },
    {
        "key": "thruplay_ctr",
        "name": "Thruplay CTR",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": true,
        "description": "Out of all those who watched the first 15s of a video, the percentage that clicked on it and left Meta properties",
        "formula": "(Outbound Clicks / VIDEO Thruplays) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["thruplay", "clicks_all"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.thruplay && theTotalEntity.clicks_outbound ? (theTotalEntity.clicks_outbound / theTotalEntity.thruplay) * 100 : 0
        }
    },
    {
        "key": "video_p100_watched",
        "name": "100% video plays",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": false,
        "percentages": false,
        "description": "The number of times your video was played to completion",
        "formula": "Count of video plays that reached 100% of total video duration",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity) => {
                const viewsOf100PercentOfTheVideo = theInsightEntity.video_p100_watched_actions?.find(action => action.action_type === 'video_view')?.value
                return viewsOf100PercentOfTheVideo ? Number(viewsOf100PercentOfTheVideo) : 0
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['video_views_p100'] ? Number(theInsightEntity['video_views_p100']) : 0,
            // LinkedIn videoCompletions = 97-100% of video — matches Meta's p100 semantic.
            linkedin_ads: (theInsightEntity) => theInsightEntity['videoCompletions'] ? Number(theInsightEntity['videoCompletions']) : 0,
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.video_p100_watched ? theTotalEntity.video_p100_watched + theCurrentEntity.video_p100_watched : theCurrentEntity.video_p100_watched
        }
    },
    {
        "key": "thumbstop",
        "name": "Thumbstop",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": false,
        "percentages": true,
        "description": "The number of times users paused their scroll to watch your video ad for at least 3 seconds.",
        "formula": "(Count of video views lasting 3 seconds or more / Impressions) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["3s_video_plays", "impressions"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity['3s_video_plays'] && theTotalEntity.impressions ? (theTotalEntity['3s_video_plays'] / theTotalEntity.impressions) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['thumbstop'] ? Number(theInsightEntity['thumbstop']) : 0
        },
    },
    {
        "key": "100percent_per_3s_video_retention",
        "name": "100%/3s video retention",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": true,
        "description": "Retention rate after watching 3 seconds of a 100% video.",
        "formula": "(100% Views / 3s Views) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["video_p100_watched", "3s_video_plays"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.video_p100_watched && theTotalEntity['3s_video_plays'] ? (theTotalEntity.video_p100_watched / theTotalEntity['3s_video_plays']) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['100percent_per_3s_video_retention'] ? Number(theInsightEntity['100percent_per_3s_video_retention']) : 0
        },
    },
    {
        "key": "cost_per_thruplay",
        "name": "Cost per thruplay",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": true,
        "percentages": false,
        "description": "The average cost you pay for each completed video view (ThruPlay) of your ad.",
        "formula": "Total amount spent / Number of Thruplays",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["spend", "thruplay"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.spend && theTotalEntity.thruplay ? theTotalEntity.spend / theTotalEntity.thruplay : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_thruplay'] ? Number(theInsightEntity['cost_per_thruplay']) : 0
        },
    },
    {
        "key": "video_plays",
        "name": "Video plays",
        "color": "bg-gradient-to-r from-pink-500 to-yellow-500",
        "fromColor": "#EC4899",
        "toColor": "#EAB308",
        "currency": false,
        "percentages": false,
        "description": "The total number of times your video ad started playing.",
        "formula": "Count of all video play events",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'video_view', 'video_play_actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['video_play_actions'] ? Number(theInsightEntity['video_play_actions']) : 0,
            google_ads: (theInsightEntity) => theInsightEntity['video_views'] ? Number(theInsightEntity['video_views']) : 0,
            // videoStarts is the literal play count (vs. videoViews which is a 2-sec hook signal).
            linkedin_ads: (theInsightEntity) => theInsightEntity['videoStarts'] ? Number(theInsightEntity['videoStarts']) : 0,
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.video_plays ? theTotalEntity.video_plays + theCurrentEntity.video_plays : theCurrentEntity.video_plays
        }
    },
    {
        "key": "1st_frame_retention",
        "name": "1st frame retention",
        "color": "bg-gradient-to-r from-teal-400 to-green-500",
        "fromColor": "#2DD4BF",
        "toColor": "#22C55E",
        "currency": false,
        "percentages": true,
        "description": "The percentage of viewers who watched the video after seeing the thumbnail",
        "formula": "(Number of viewers who watched past first frame / Impression) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["video_plays", "impressions"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.video_plays && theTotalEntity.impressions ? (theTotalEntity.video_plays / theTotalEntity.impressions) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['1st_frame_retention'] ? Number(theInsightEntity['1st_frame_retention']) : 0
        },
    },
    {
        "key": "thumbstop_click_rate",
        "name": "Thumbstop click rate",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": true,
        "description": "The percentage of thumbstops that resulted in clicks on your ad.",
        "formula": "(Link Clicks / Video Views at least 3s) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["link_clicks", "3s_video_plays"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.link_clicks && theTotalEntity['3s_video_plays'] ? (theTotalEntity.link_clicks / theTotalEntity['3s_video_plays']) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['thumbstop_click_rate'] ? Number(theInsightEntity['thumbstop_click_rate']) : 0
        },
    },
    {
        "key": "cost_per_3s_plays",
        "name": "Cost per 3s plays",
        "color": "bg-gradient-to-r from-indigo-500 to-purple-500",
        "fromColor": "#6366F1",
        "toColor": "#A855F7",
        "currency": true,
        "percentages": false,
        "description": "The average cost you pay for each 3-second video view.",
        "formula": "Spend / Number of 3-second video plays",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["spend", "3s_video_plays"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.spend && theTotalEntity['3s_video_plays'] ? theTotalEntity.spend / theTotalEntity['3s_video_plays'] : 0
        },
    },
    {
        "key": "video_p25_watched",
        "name": "25% video plays",
        "color": "bg-gradient-to-r from-pink-500 to-yellow-500",
        "fromColor": "#EC4899",
        "toColor": "#EAB308",
        "currency": false,
        "percentages": false,
        "description": "The number of times your video was played to 25% of its length.",
        "formula": "Count of video plays that reached 25% of total video duration",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity) => {
                const viewsOf25PercentOfTheVideo = theInsightEntity.video_p25_watched_actions?.find(action => action.action_type === 'video_view')?.value
                return viewsOf25PercentOfTheVideo ? Number(viewsOf25PercentOfTheVideo) : 0
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['video_views_p25'] ? Number(theInsightEntity['video_views_p25']) : 0,
            linkedin_ads: (theInsightEntity) => theInsightEntity['videoFirstQuartileCompletions'] ? Number(theInsightEntity['videoFirstQuartileCompletions']) : 0,
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.video_p25_watched ? theTotalEntity.video_p25_watched + theCurrentEntity.video_p25_watched : theCurrentEntity.video_p25_watched
        }
    },
    {
        "key": "video_p50_watched",
        "name": "50% video plays",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": false,
        "percentages": false,
        "description": "The number of times your video was played to 50% of its length.",
        "formula": "Count of video plays that reached 50% of total video duration",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity) => {
                const viewsOf50PercentOfTheVideo = theInsightEntity.video_p50_watched_actions?.find(action => action.action_type === 'video_view')?.value
                return viewsOf50PercentOfTheVideo ? Number(viewsOf50PercentOfTheVideo) : 0
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['video_views_p50'] ? Number(theInsightEntity['video_views_p50']) : 0,
            linkedin_ads: (theInsightEntity) => theInsightEntity['videoMidpointCompletions'] ? Number(theInsightEntity['videoMidpointCompletions']) : 0,
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.video_p50_watched ? theTotalEntity.video_p50_watched + theCurrentEntity.video_p50_watched : theCurrentEntity.video_p50_watched
        }
    },
    {
        "key": "video_p75_watched",
        "name": "75% video plays",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": false,
        "description": "The number of times your video was played to 75% of its length.",
        "formula": "Count of video plays that reached 75% of total video duration",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity) => {
                const viewsOf75PercentOfTheVideo = theInsightEntity.video_p75_watched_actions?.find(action => action.action_type === 'video_view')?.value
                return viewsOf75PercentOfTheVideo ? Number(viewsOf75PercentOfTheVideo) : 0
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['video_views_p75'] ? Number(theInsightEntity['video_views_p75']) : 0,
            linkedin_ads: (theInsightEntity) => theInsightEntity['videoThirdQuartileCompletions'] ? Number(theInsightEntity['videoThirdQuartileCompletions']) : 0,
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.video_p75_watched ? theTotalEntity.video_p75_watched + theCurrentEntity.video_p75_watched : theCurrentEntity.video_p75_watched
        }
    },
    {
        "key": "video_p95_watched",
        "name": "95% video plays",
        "color": "bg-gradient-to-r from-indigo-500 to-purple-500",
        "fromColor": "#6366F1",
        "toColor": "#A855F7",
        "currency": false,
        "percentages": false,
        "description": "The number of times your video was played to 95% of its length.",
        "formula": "Count of video plays that reached 95% of total video duration",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity) => {
                const viewsOf95PercentOfTheVideo = theInsightEntity.video_p95_watched_actions?.find(action => action.action_type === 'video_view')?.value
                return viewsOf95PercentOfTheVideo ? Number(viewsOf95PercentOfTheVideo) : 0
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['video_views_p100'] ? Number(theInsightEntity['video_views_p100']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.video_p95_watched ? theTotalEntity.video_p95_watched + theCurrentEntity.video_p95_watched : theCurrentEntity.video_p95_watched
        }
    },
    {
        "key": "video_p25_play_rate",
        "name": "25% video plays (rate)",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": false,
        "percentages": true,
        "description": "The percentage of video impressions that reached 25% of the video length.",
        "formula": "(Number of 25% video plays / Total video impressions) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["video_p25_watched", "impressions"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.video_p25_watched && theTotalEntity.impressions ? (theTotalEntity.video_p25_watched / theTotalEntity.impressions) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => {
                const videoViewsP25 = Number(theInsightEntity['video_views_p25'] || 0)
                const impressions = Number(theInsightEntity['impressions'] || 0)
                return impressions > 0 ? (videoViewsP25 / impressions) * 100 : 0
            }
        },
    },
    {
        "key": "video_p50_play_rate",
        "name": "50% video plays (rate)",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": false,
        "percentages": true,
        "description": "The percentage of video impressions that reached 50% of the video length.",
        "formula": "Number of 50% video plays / Total video impressions \u00d7 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["video_p50_watched", "impressions"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.video_p50_watched && theTotalEntity.impressions ? (theTotalEntity.video_p50_watched / theTotalEntity.impressions) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => {
                const videoViewsP50 = Number(theInsightEntity['video_views_p50'] || 0)
                const impressions = Number(theInsightEntity['impressions'] || 0)
                return impressions > 0 ? (videoViewsP50 / impressions) * 100 : 0
            }
        },
    },
    {
        "key": "video_p75_play_rate",
        "name": "75% video plays (rate)",
        "color": "bg-gradient-to-r from-pink-500 to-yellow-500",
        "fromColor": "#EC4899",
        "toColor": "#EAB308",
        "currency": false,
        "percentages": true,
        "description": "The percentage of video impressions that reached 75% of the video length.",
        "formula": "Number of 75% video plays / Total video impressions \u00d7 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["video_p75_watched", "impressions"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.video_p75_watched && theTotalEntity.impressions ? (theTotalEntity.video_p75_watched / theTotalEntity.impressions) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => {
                const videoViewsP75 = Number(theInsightEntity['video_views_p75'] || 0)
                const impressions = Number(theInsightEntity['impressions'] || 0)
                return impressions > 0 ? (videoViewsP75 / impressions) * 100 : 0
            }
        },
    },
    {
        "key": "video_p95_play_rate",
        "name": "95% video plays (rate)",
        "color": "bg-gradient-to-r from-pink-500 to-yellow-500",
        "fromColor": "#EC4899",
        "toColor": "#EAB308",
        "currency": false,
        "percentages": true,
        "description": "The percentage of video impressions that reached 95% of the video length.",
        "formula": "Number of 95% video plays / Total video impressions \u00d7 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["video_p95_watched", "impressions"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.video_p95_watched && theTotalEntity.impressions ? (theTotalEntity.video_p95_watched / theTotalEntity.impressions) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => {
                const videoViewsP100 = Number(theInsightEntity['video_views_p100'] || 0)
                const impressions = Number(theInsightEntity['impressions'] || 0)
                return impressions > 0 ? (videoViewsP100 / impressions) * 100 : 0
            }
        },
    },
    {
        "key": "video_p100_play_rate",
        "name": "100% video plays (rate)",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": false,
        "percentages": true,
        "description": "The percentage of video impressions that reached 100% of the video length.",
        "formula": "(Number of 100% video plays / Impressions) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["video_p100_watched", "impressions"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.video_p100_watched && theTotalEntity.impressions ? (theTotalEntity.video_p100_watched / theTotalEntity.impressions) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => {
                const videoViewsP100 = Number(theInsightEntity['video_views_p100'] || 0)
                const impressions = Number(theInsightEntity['impressions'] || 0)
                return impressions > 0 ? (videoViewsP100 / impressions) * 100 : 0
            }
        },
    },
    {
        "key": "25percent_to_50percent_video_plays_drop_off_rate",
        "name": "25% to 50% video plays drop off rate",
        "color": "bg-gradient-to-r from-teal-400 to-green-500",
        "fromColor": "#2DD4BF",
        "toColor": "#22C55E",
        "currency": false,
        "percentages": true,
        "description": "The percentage of viewers who stopped watching between 25% and 50% of the video length.",
        "formula": "((Number of 25% plays - Number of 50% plays) / Number of 25% plays) * 100",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["video_p25_watched", "video_p50_watched"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.video_p25_watched && theTotalEntity.video_p50_watched ? ((theTotalEntity.video_p25_watched - theTotalEntity.video_p50_watched) / theTotalEntity.video_p25_watched) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => {
                const videoViewsP25 = Number(theInsightEntity['video_views_p25'] || 0)
                const videoViewsP50 = Number(theInsightEntity['video_views_p50'] || 0)
                return videoViewsP25 > 0 ? ((videoViewsP25 - videoViewsP50) / videoViewsP25) * 100 : 0
            }
        },
    },
    {
        "key": "50percent_to_75percent_video_plays_drop_off_rate",
        "name": "50% to 75% video plays drop off rate",
        "color": "bg-gradient-to-r from-pink-500 to-yellow-500",
        "fromColor": "#EC4899",
        "toColor": "#EAB308",
        "currency": false,
        "percentages": true,
        "description": "The percentage of viewers who stopped watching between 50% and 75% of the video length.",
        "formula": "((Number of 50% plays - Number of 75% plays) / Number of 50% plays) * 100",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["video_p50_watched", "video_p75_watched"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.video_p50_watched && theTotalEntity.video_p75_watched ? ((theTotalEntity.video_p50_watched - theTotalEntity.video_p75_watched) / theTotalEntity.video_p50_watched) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => {
                const videoViewsP50 = Number(theInsightEntity['video_views_p50'] || 0)
                const videoViewsP75 = Number(theInsightEntity['video_views_p75'] || 0)
                return videoViewsP50 > 0 ? ((videoViewsP50 - videoViewsP75) / videoViewsP50) * 100 : 0
            }
        },
    },
    {
        "key": "75percent_to_95percent_video_plays_drop_off_rate",
        "name": "75% to 95% video plays drop off rate",
        "color": "bg-gradient-to-r from-pink-500 to-yellow-500",
        "fromColor": "#EC4899",
        "toColor": "#EAB308",
        "currency": false,
        "percentages": true,
        "description": "The percentage of viewers who stopped watching between 75% and 95% of the video length.",
        "formula": "((Number of 75% plays - Number of 95% plays) / Number of 75% plays) * 100",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["video_p75_watched", "video_p95_watched"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.video_p75_watched && theTotalEntity.video_p95_watched ? ((theTotalEntity.video_p75_watched - theTotalEntity.video_p95_watched) / theTotalEntity.video_p75_watched) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => {
                const videoViewsP75 = Number(theInsightEntity['video_views_p75'] || 0)
                const videoViewsP100 = Number(theInsightEntity['video_views_p100'] || 0)
                return videoViewsP75 > 0 ? ((videoViewsP75 - videoViewsP100) / videoViewsP75) * 100 : 0
            }
        },
    },
    {
        "key": "75percent_to_100percent_video_plays_drop_off_rate",
        "name": "75% to 100% video plays drop off rate",
        "color": "bg-gradient-to-r from-indigo-500 to-purple-500",
        "fromColor": "#6366F1",
        "toColor": "#A855F7",
        "currency": false,
        "percentages": true,
        "description": "The percentage of viewers who stopped watching between 75% and 100% of the video length.",
        "formula": "((Number of 75% plays - Number of 100% plays) / Number of 75% plays) * 100",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["video_p75_watched", "video_p100_watched"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.video_p75_watched && theTotalEntity.video_p100_watched ? ((theTotalEntity.video_p75_watched - theTotalEntity.video_p100_watched) / theTotalEntity.video_p75_watched) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => {
                const videoViewsP75 = Number(theInsightEntity['video_views_p75'] || 0)
                const videoViewsP100 = Number(theInsightEntity['video_views_p100'] || 0)
                return videoViewsP75 > 0 ? ((videoViewsP75 - videoViewsP100) / videoViewsP75) * 100 : 0
            }
        },
    },
    {
        "key": "95percent_to_100percent_video_plays_drop_off_rate",
        "name": "95% to 100% video plays drop off rate",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": false,
        "percentages": true,
        "description": "The percentage of viewers who stopped watching between 95% and 100% of the video length.",
        "formula": "((Number of 95% plays - Number of 100% plays) / Number of 95% plays) * 100",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["video_p95_watched", "video_p100_watched"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.video_p95_watched && theTotalEntity.video_p100_watched ? ((theTotalEntity.video_p95_watched - theTotalEntity.video_p100_watched) / theTotalEntity.video_p95_watched) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => {
                const videoViewsP100 = Number(theInsightEntity['video_views_p100'] || 0)
                const videoViewsP75 = Number(theInsightEntity['video_views_p75'] || 0)
                return videoViewsP75 > 0 ? ((videoViewsP75 - videoViewsP100) / videoViewsP75) * 100 : 0
            }
        },
    },
    {
        "key": "sustain_rate",
        "name": "Sustain rate",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": false,
        "percentages": true,
        "description": "The percentage of users who continue to watch your video after the initial engagement.",
        "formula": "(1 - ((Video Watches At 25% - Video Watches At 95%)/ Video Watches At 25%)) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["video_p25_watched", "video_p95_watched"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.video_p25_watched && theTotalEntity.video_p95_watched ? (1 - ((theTotalEntity.video_p25_watched - theTotalEntity.video_p95_watched) / theTotalEntity.video_p25_watched)) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['sustain_rate'] ? Number(theInsightEntity['sustain_rate']) : 0
        },
    },
    {
        "key": "hook_to_25",
        "name": "Hook to 25%",
        "color": "bg-gradient-to-r from-indigo-500 to-purple-500",
        "fromColor": "#6366F1",
        "toColor": "#A855F7",
        "currency": false,
        "percentages": true,
        "description": "The percentage of viewers who kept watching past the hook and reached 25% of the video length.",
        "formula": "video_p25_watched / video_3s_plays",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["video_p25_watched", "3s_video_plays"],
        extractors: {
            tiktok_ads: (theInsightEntity) => {
                const videoViewsP25 = Number(theInsightEntity['video_views_p25'] || 0)
                const videoWatched2s = Number(theInsightEntity['video_watched_2s'] || 0)
                return videoWatched2s > 0 ? (videoViewsP25 / videoWatched2s) * 100 : 0
            }
        },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity['3s_video_plays'] && theTotalEntity['video_p25_watched'] ? (theTotalEntity['video_p25_watched'] / theTotalEntity['3s_video_plays']) * 100 : 0
        },
    },
    {
        "key": "video_50_to_25",
        "name": "Video Plays 50% / 25%",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "percentages": true,
        "description": "The percentage of viewers who watched from 25% to at least halfway (50%) of the video.",
        "formula": "video_p50_watched / video_p25_watched",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["video_p50_watched", "video_p25_watched"],
        extractors: {
            tiktok_ads: (theInsightEntity) => {
                const videoViewsP50 = Number(theInsightEntity['video_views_p50'] || 0)
                const videoViewsP25 = Number(theInsightEntity['video_views_p25'] || 0)
                return videoViewsP25 > 0 ? (videoViewsP50 / videoViewsP25) * 100 : 0
            }
        },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity['video_p50_watched'] && theTotalEntity['video_p25_watched'] ? (theTotalEntity['video_p50_watched'] / theTotalEntity['video_p25_watched']) * 100 : 0
        },
    },
    {
        "key": "video_75_to_50",
        "name": "Video Plays 75% / 50%",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "percentages": true,
        "description": "The percentage of viewers who continued watching from halfway to 75% of the video.",
        "formula": "video_p75_watched / video_p50_watched",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["video_p75_watched", "video_p50_watched"],
        extractors: {
            tiktok_ads: (theInsightEntity) => {
                const videoViewsP75 = Number(theInsightEntity['video_views_p75'] || 0)
                const videoViewsP50 = Number(theInsightEntity['video_views_p50'] || 0)
                return videoViewsP50 > 0 ? (videoViewsP75 / videoViewsP50) * 100 : 0
            }
        },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity['video_p75_watched'] && theTotalEntity['video_p50_watched'] ? (theTotalEntity['video_p75_watched'] / theTotalEntity['video_p50_watched']) * 100 : 0
        },
    },
    {
        "key": "video_100_to_75",
        "name": "Video Plays 100% / 75%",
        "color": "bg-gradient-to-r from-indigo-500 to-purple-500",
        "fromColor": "#6366F1",
        "toColor": "#A855F7",
        "percentages": true,
        "description": "The percentage of viewers who continued watching from 75% to full length of the video.",
        "formula": "video_p100_watched / video_p75_watched",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["video_p100_watched", "video_p75_watched"],
        extractors: {
            tiktok_ads: (theInsightEntity) => {
                const videoViewsP100 = Number(theInsightEntity['video_views_p100'] || 0)
                const videoViewsP75 = Number(theInsightEntity['video_views_p75'] || 0)
                return videoViewsP75 > 0 ? (videoViewsP100 / videoViewsP75) * 100 : 0
            }
        },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity['video_p100_watched'] && theTotalEntity['video_p75_watched'] ? (theTotalEntity['video_p100_watched'] / theTotalEntity['video_p75_watched']) * 100 : 0
        },
    },
    {
        "key": "atc",
        "name": "ATC",
        "color": "bg-gradient-to-r from-indigo-500 to-purple-500",
        "fromColor": "#6366F1",
        "toColor": "#A855F7",
        "currency": false,
        "percentages": false,
        "description": "Total number of 'Add to Cart' events.",
        "formula": "Sum of Add to Cart Events",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'add_to_cart', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['web_event_add_to_cart'] ? Number(theInsightEntity['web_event_add_to_cart']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.atc ? theTotalEntity.atc + theCurrentEntity.atc : theCurrentEntity.atc
        }
    },
    {
        "key": "click_to_atc_ratio",
        "name": "Click to ATC ratio",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": false,
        "percentages": true,
        "description": "The percentage of clicks that result in items being added to cart.",
        "formula": "Add to cart events / Total clicks \u00d7 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["atc", "clicks_all"],
        "needVerify": true,
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.atc && theTotalEntity.clicks_all ? (theTotalEntity.atc / theTotalEntity.clicks_all) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['click_to_atc_ratio'] ? Number(theInsightEntity['click_to_atc_ratio']) : 0
        },
    },
    {
        "key": "atc_to_purchase_ratio",
        "name": "ATC to purchase ratio",
        "color": "bg-gradient-to-r from-pink-500 to-yellow-500",
        "fromColor": "#EC4899",
        "toColor": "#EAB308",
        "currency": false,
        "percentages": true,
        "description": "The percentage of add to cart events that convert into purchases.",
        "formula": "(Purchase events / Add to cart events) * 100",
        "bestValue": "high",
        "needVerify": true,
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["purchase", "atc"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.purchases && theTotalEntity.atc ? (theTotalEntity.purchases / theTotalEntity.atc) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['atc_to_purchase_ratio'] ? Number(theInsightEntity['atc_to_purchase_ratio']) : 0
        },
    },
    {
        "key": "website_atc_count",
        "name": "Website ATC count",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": false,
        "percentages": false,
        "description": "Total number of 'Add to Cart' events on the website.",
        "formula": "Sum of Website Add to Cart Events",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'offsite_conversion.fb_pixel_add_to_cart', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['website_atc_count'] ? Number(theInsightEntity['website_atc_count']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.website_atc_count ? theTotalEntity.website_atc_count + theCurrentEntity.website_atc_count : theCurrentEntity.website_atc_count
        }
    },
    {
        "key": "click_to_website_atc",
        "name": "Click to website ATC",
        "color": "bg-gradient-to-r from-teal-400 to-green-500",
        "fromColor": "#2DD4BF",
        "toColor": "#22C55E",
        "currency": false,
        "percentages": true,
        "description": "Number of clicks that resulted in an 'Add to Cart' action on the website.",
        "formula": "Website Add to Cart Actions from Clicks",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        "dependsOn": ["website_atc_count", "clicks_all"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.website_atc_count && theTotalEntity.clicks_all ? theTotalEntity.website_atc_count / theTotalEntity.clicks_all : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['click_to_website_atc'] ? Number(theInsightEntity['click_to_website_atc']) : 0
        },
    },
    {
        "key": "add_to_cart_to_purchase_ratio_website",
        "name": "Add to cart to purchase ratio (website)",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": true,
        "description": "Percentage of 'Add to Cart' events that led to a purchase on the website.",
        "formula": "(Website Purchases / Website Add To Carts) * 100",
        "bestValue": "high",
        "needVerify": true,
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["purchase", "website_atc_count"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.purchases && theTotalEntity.website_atc_count ? (theTotalEntity.purchases / theTotalEntity.website_atc_count) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['add_to_cart_to_purchase_ratio_website'] ? Number(theInsightEntity['add_to_cart_to_purchase_ratio_website']) : 0
        },
    },
    {
        "key": "view_content",
        "name": "View content",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": false,
        "percentages": false,
        "description": "Total number of content views.",
        "formula": "Sum of Content Views",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'view_content', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['view_content'] ? Number(theInsightEntity['view_content']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.view_content ? theTotalEntity.view_content + theCurrentEntity.view_content : theCurrentEntity.view_content
        }
    },
    {
        "key": "view_content_to_atc_ratio",
        "name": "View content to ATC ratio",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": false,
        "percentages": true,
        "description": "Percentage of all times view content was clicked, the percent of times a user triggered an ad to cart event",
        "formula": "(Add To Cart / View Content) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        "dependsOn": ["atc", "view_content"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.atc && theTotalEntity.view_content ? (theTotalEntity.atc / theTotalEntity.view_content) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['view_content_to_atc_ratio'] ? Number(theInsightEntity['view_content_to_atc_ratio']) : 0
        },
    },
    {
        "key": "landing_page_views",
        "name": "Landing page views",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": false,
        "percentages": false,
        "description": "Total number of times the landing page was viewed.",
        "formula": "Sum of Landing Page Views",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'landing_page_view', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['landing_page_views'] ? Number(theInsightEntity['landing_page_views']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.landing_page_views ? theTotalEntity.landing_page_views + theCurrentEntity.landing_page_views : theCurrentEntity.landing_page_views
        }
    },
    {
        "key": "cost_per_landing_page_view",
        "name": "Cost per landing page view",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": true,
        "percentages": false,
        "description": "Average cost for each landing page view.",
        "formula": "Spend / Landing Page Views",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'landing_page_view', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_landing_page_view'] ? Number(theInsightEntity['cost_per_landing_page_view']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_landing_page_view ? theTotalEntity.cost_per_landing_page_view + theCurrentEntity.cost_per_landing_page_view : theCurrentEntity.cost_per_landing_page_view
        }
    },
    {
        "key": "view_content_value",
        "name": "View content value",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": true,
        "percentages": false,
        "description": "Total value generated from content views.",
        "formula": "Sum of Content View Values",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'view_content', 'action_values', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['view_content_value'] ? Number(theInsightEntity['view_content_value']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.view_content_value ? theTotalEntity.view_content_value + theCurrentEntity.view_content_value : theCurrentEntity.view_content_value
        }

    },
    {
        "key": "cost_per_view_content",
        "name": "Cost per View Content",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": true,
        "percentages": false,
        "description": "Average cost per content view event.",
        "formula": "Total Spend / Content Views",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'view_content', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_view_content'] ? Number(theInsightEntity['cost_per_view_content']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_view_content ? theTotalEntity.cost_per_view_content + theCurrentEntity.cost_per_view_content : theCurrentEntity.cost_per_view_content
        }
    },
    {
        "key": "atc_value",
        "name": "ATC value",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": true,
        "percentages": false,
        "description": "Total value of items added to cart.",
        "formula": "Sum of Values from Add to Cart Events",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'add_to_cart', 'action_values', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['total_web_event_add_to_cart_value'] ? Number(theInsightEntity['total_web_event_add_to_cart_value']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.atc_value ? theTotalEntity.atc_value + theCurrentEntity.atc_value : theCurrentEntity.atc_value
        }
    },
    {
        "key": "cost_per_atc",
        "name": "Cost per ATC",
        "color": "bg-gradient-to-r from-teal-400 to-green-500",
        "fromColor": "#2DD4BF",
        "toColor": "#22C55E",
        "currency": true,
        "percentages": false,
        "description": "Average cost per 'Add to Cart' event.",
        "formula": "Total Spend / Add to Cart Events",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'add_to_cart', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_web_event_add_to_cart'] ? Number(theInsightEntity['cost_per_web_event_add_to_cart']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_atc ? theTotalEntity.cost_per_atc + theCurrentEntity.cost_per_atc : theCurrentEntity.cost_per_atc
        }
    },
    {
        "key": "ic",
        "name": "IC (Initial Checkout)",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": false,
        "percentages": false,
        "description": "Total number of 'Initiate Checkout' events.",
        "formula": "Sum of Initiate Checkout Events",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'initiate_checkout', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['initiate_checkout'] ? Number(theInsightEntity['initiate_checkout']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.ic ? theTotalEntity.ic + theCurrentEntity.ic : theCurrentEntity.ic
        }
    },
    {
        "key": "ic_value",
        "name": "IC Value",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": true,
        "percentages": false,
        "description": "Total value associated with 'Initiate Checkout' events.",
        "formula": "Sum of Values from Initiate Checkout Events",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'initiate_checkout', 'action_values', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['total_initiate_checkout_value'] ? Number(theInsightEntity['total_initiate_checkout_value']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.ic_value ? theTotalEntity.ic_value + theCurrentEntity.ic_value : theCurrentEntity.ic_value
        }
    },
    {
        "key": "cost_per_ic",
        "name": "Cost per IC",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": true,
        "percentages": false,
        "description": "Average cost per Initiate Checkout event.",
        "formula": "Total Spend / Initial Checkout",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'initiate_checkout', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_initiate_checkout'] ? Number(theInsightEntity['cost_per_initiate_checkout']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_ic ? theTotalEntity.cost_per_ic + theCurrentEntity.cost_per_ic : theCurrentEntity.cost_per_ic
        }
    },
    {
        "key": "cost_per_website_purchase",
        "name": "Cost per website purchase",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": true,
        "percentages": false,
        "description": "Average cost per purchase on the website.",
        "formula": "Total Spend / Website Purchases",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'purchase', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_website_purchase'] ? Number(theInsightEntity['cost_per_website_purchase']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_website_purchase ? theTotalEntity.cost_per_website_purchase + theCurrentEntity.cost_per_website_purchase : theCurrentEntity.cost_per_website_purchase
        }
    },
    {
        "key": "searches",
        "name": "Searches",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": false,
        "description": "Total number of searches.",
        "formula": "Sum of Searches",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'search', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['searches'] ? Number(theInsightEntity['searches']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.searches ? theTotalEntity.searches + theCurrentEntity.searches : theCurrentEntity.searches
        },
    },
    {
        "key": "cost_per_search",
        "name": "Cost per search",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": true,
        "percentages": false,
        "description": "Average cost per search.",
        "formula": "Total Spend / Searches",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'search', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_search'] ? Number(theInsightEntity['cost_per_search']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_search ? theTotalEntity.cost_per_search + theCurrentEntity.cost_per_search : theCurrentEntity.cost_per_search
        },
    },
    {
        "key": "click_quality",
        "name": "Click quality",
        "color": "bg-gradient-to-r from-pink-500 to-yellow-500",
        "fromColor": "#EC4899",
        "toColor": "#EAB308",
        "currency": false,
        "percentages": true,
        "description": "A measure of how likely people are to engage with your ad after clicking it.",
        "formula": "(Landing Page Views / Outbound Clicks) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        "dependsOn": ["landing_page_views", "clicks_outbound"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.landing_page_views && theTotalEntity.clicks_outbound ? (theTotalEntity.landing_page_views / theTotalEntity.clicks_outbound) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['click_quality'] ? Number(theInsightEntity['click_quality']) : 0
        },
    },
    {
        "key": "app_installs",
        "name": "App installs",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": false,
        "description": "Total number of app installations.",
        "formula": "Sum of App Installs",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'omni_app_install', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['app_install'] ? Number(theInsightEntity['app_install']) : 0,
            axon: (theInsightEntity) => theInsightEntity['conversions'] ? Number(theInsightEntity['conversions']) : theInsightEntity['sales'] ? Number(theInsightEntity['sales']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.app_installs ? theTotalEntity.app_installs + theCurrentEntity.app_installs : theCurrentEntity.app_installs
        },
    },
    {
        "key": "cost_per_app_install",
        "name": "Cost per app install",
        "color": "bg-gradient-to-r from-indigo-500 to-purple-500",
        "fromColor": "#6366F1",
        "toColor": "#A855F7",
        "currency": true,
        "percentages": false,
        "description": "Average cost per app installation.",
        "formula": "Total Spend / App Installs",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        "dependsOn": ["spend", "app_installs"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.spend && theTotalEntity.app_installs ? (theTotalEntity.spend / theTotalEntity.app_installs) : 0
        },
    },
    {
        "key": "ipm",
        "name": "IPM",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": false,
        "description": "Installs per 1,000 impressions.",
        "formula": "(Total Installs / Impressions) * 1000",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        "dependsOn": ["app_installs", "impressions"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.app_installs && theTotalEntity.impressions ? (theTotalEntity.app_installs / theTotalEntity.impressions) * 1000 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['ipm'] ? Number(theInsightEntity['ipm']) : 0
        },
    },
    {
        "key": "click_to_app_install",
        "name": "Click to app install",
        "color": "bg-gradient-to-r from-indigo-500 to-purple-500",
        "fromColor": "#6366F1",
        "toColor": "#A855F7",
        "currency": false,
        "percentages": true,
        "description": "The percentage of clicks that result in app installations.",
        "formula": "(Amount of App Installs / Link Clicks) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["app_installs", "clicks_all"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.app_installs && theTotalEntity.clicks_all ? (theTotalEntity.app_installs / theTotalEntity.clicks_all) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['click_to_app_install'] ? Number(theInsightEntity['click_to_app_install']) : 0
        },
    },
    {
        "key": "achievement_unlocked",
        "name": "Achievement unlocked",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": false,
        "description": "Total number of achievements unlocked by users.",
        "formula": "Sum of Achievements Unlocked",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'omni_achievement_unlocked', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['achievement_unlocked'] ? Number(theInsightEntity['achievement_unlocked']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.achievement_unlocked ? theTotalEntity.achievement_unlocked + theCurrentEntity.achievement_unlocked : theCurrentEntity.achievement_unlocked
        },
    },
    {
        "key": "achievement_unlocked_value",
        "name": "Achievement unlocked value",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": true,
        "percentages": false,
        "description": "Total value associated with unlocked achievements.",
        "formula": "Sum of Values from Achievements Unlocked",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'omni_achievement_unlocked', 'action_values', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['achievement_unlocked_value'] ? Number(theInsightEntity['achievement_unlocked_value']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.achievement_unlocked ? theTotalEntity.achievement_unlocked + theCurrentEntity.achievement_unlocked : theCurrentEntity.achievement_unlocked
        },
    },
    {
        "key": "cost_per_achievement_unlocked",
        "name": "Cost per achievement unlocked",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "currency": true,
        "percentages": false,
        "description": "Average cost per achievement unlocked.",
        "formula": "Total Spend / Achievements Unlocked",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'omni_achievement_unlocked', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_achievement_unlocked'] ? Number(theInsightEntity['cost_per_achievement_unlocked']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_achievement_unlocked ? theTotalEntity.cost_per_achievement_unlocked + theCurrentEntity.cost_per_achievement_unlocked : theCurrentEntity.cost_per_achievement_unlocked
        },
    },
    {
        "key": "levels_achieved",
        "name": "Levels achieved",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": false,
        "description": "Total number of levels achieved by users.",
        "formula": "Sum of Levels Achieved",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'omni_level_achieved', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['levels_achieved'] ? Number(theInsightEntity['levels_achieved']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.levels_achieved ? theTotalEntity.levels_achieved + theCurrentEntity.levels_achieved : theCurrentEntity.levels_achieved
        },
    },
    {
        "key": "levels_achieved_value",
        "name": "Levels achieved value",
        "color": "bg-gradient-to-r from-pink-500 to-yellow-500",
        "fromColor": "#EC4899",
        "toColor": "#EAB308",
        "currency": true,
        "percentages": false,
        "description": "Total value associated with levels achieved.",
        "formula": "Sum of Values from Levels Achieved",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'omni_level_achieved', 'action_values', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['levels_achieved_value'] ? Number(theInsightEntity['levels_achieved_value']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.levels_achieved_value ? theTotalEntity.levels_achieved_value + theCurrentEntity.levels_achieved_value : theCurrentEntity.levels_achieved_value
        },
    },
    {
        "key": "cost_per_level_achieved",
        "name": "Cost per level achieved",
        "color": "bg-gradient-to-r from-pink-500 to-yellow-500",
        "fromColor": "#EC4899",
        "toColor": "#EAB308",
        "currency": true,
        "percentages": false,
        "description": "Average cost to achieve each level.",
        "formula": "Total Spend / Levels Achieved",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'omni_level_achieved', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_level_achieved'] ? Number(theInsightEntity['cost_per_level_achieved']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_level_achieved ? theTotalEntity.cost_per_level_achieved + theCurrentEntity.cost_per_level_achieved : theCurrentEntity.cost_per_level_achieved
        },
    },
    {
        "key": "appointments_scheduled",
        "name": "Appointments scheduled",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": false,
        "description": "Total number of scheduled appointments.",
        "formula": "Sum of Appointments Scheduled",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'schedule_total', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['appointments_scheduled'] ? Number(theInsightEntity['appointments_scheduled']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.appointments_scheduled ? theTotalEntity.appointments_scheduled + theCurrentEntity.appointments_scheduled : theCurrentEntity.appointments_scheduled
        },
    },
    {
        "key": "appointments_scheduled_conversion_value",
        "name": "Appointments scheduled conversion value",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": true,
        "percentages": false,
        "description": "Total conversion value from scheduled appointments.",
        "formula": "Sum of Values from Appointments Scheduled",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                // The 'schedule_total' refer to an Appointments scheduled by Meta docs.
                // Not sure it's correct...
                return extractActionsPropertiesByType(theInsightEntity, 'schedule_total', 'action_values', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['appointments_scheduled_conversion_value'] ? Number(theInsightEntity['appointments_scheduled_conversion_value']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.appointments_scheduled_conversion_value ? theTotalEntity.appointments_scheduled_conversion_value + theCurrentEntity.appointments_scheduled_conversion_value : theCurrentEntity.appointments_scheduled_conversion_value
        },
    },
    {
        "key": "cost_per_appointment_scheduled",
        "name": "Cost per appointment scheduled",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "currency": true,
        "percentages": false,
        "description": "Average cost per scheduled appointment.",
        "formula": "Total Spend / Appointments Scheduled",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                // The 'schedule_total' refer to an Appointments scheduled by Meta docs.
                // Not sure it's correct...
                return extractActionsPropertiesByType(theInsightEntity, 'schedule_total', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_appointment_scheduled'] ? Number(theInsightEntity['cost_per_appointment_scheduled']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_appointment_scheduled ? theTotalEntity.cost_per_appointment_scheduled + theCurrentEntity.cost_per_appointment_scheduled : theCurrentEntity.cost_per_appointment_scheduled
        },
    },
    {
        "key": "click_to_appointments_scheduled",
        "name": "Click to appointments scheduled",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": false,
        "percentages": true,
        "description": "The percentage of clicks that result in scheduled appointments.",
        "formula": "(Appointment scheduling events / Total outbound clicks)",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["appointments_scheduled", "clicks_outbound"],
        calculationFunction: (theTotalEntity) => {
            // Does it need to be a percentage?
            return theTotalEntity.appointments_scheduled && theTotalEntity.clicks_outbound ? (theTotalEntity.appointments_scheduled / theTotalEntity.clicks_outbound) * 100 : 0
        },

        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['click_to_appointments_scheduled'] ? Number(theInsightEntity['click_to_appointments_scheduled']) : 0
        },
    },
    {
        "key": "leads",
        "name": "Leads",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": false,
        "description": "Total number of leads generated.",
        "formula": "Sum of Leads",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'lead', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['form'] ? Number(theInsightEntity['form']) : 0,
            // LinkedIn "Leads" = One Click Lead Gen submissions. This also enables the derived
            // cost_per_lead and click_to_leads columns for LinkedIn (they depend on `leads`).
            linkedin_ads: (theInsightEntity) => theInsightEntity['oneClickLeads'] ? Number(theInsightEntity['oneClickLeads']) : 0,
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.leads ? theTotalEntity.leads + theCurrentEntity.leads : theCurrentEntity.leads
        },
    },
    {
        "key": "leads_value",
        "name": "Leads value",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "currency": true,
        "percentages": false,
        "description": "Total value from leads.",
        "formula": "Sum of Lead Values",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'lead', 'action_values', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['total_form_value'] ? Number(theInsightEntity['total_form_value']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.leads_value ? theTotalEntity.leads_value + theCurrentEntity.leads_value : theCurrentEntity.leads_value
        },
    },
    {
        "key": "cost_per_lead",
        "name": "Cost per lead",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": true,
        "percentages": false,
        "description": "Average cost per lead generated.",
        "formula": "Total Spend / Leads",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,

        "dependsOn": ["spend", "leads"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.spend && theTotalEntity.leads ? theTotalEntity.spend / theTotalEntity.leads : 0
        },
        // metaExtractFunction: (theInsightEntity, attributionWindowKeys) => {
        //     return extractActionsPropertiesByType(theInsightEntity, 'lead', 'cost_per_action_type', attributionWindowKeys)
        // },
        // calculationFunction: (theTotalEntity, theCurrentEntity) => {
        //     return theTotalEntity.cost_per_lead ? theTotalEntity.cost_per_lead + theCurrentEntity.cost_per_lead : theCurrentEntity.cost_per_lead
        // },
        // extractors: {
        //     tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_form'] ? Number(theInsightEntity['cost_per_form']) : 0
        // },
    },
    {
        "key": "click_to_leads",
        "name": "Click to leads",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": false,
        "percentages": true,
        "description": "The percentage of clicks that generate leads.",
        "formula": "(Lead generation events / Total clicks) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["leads", "clicks_all"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.leads && theTotalEntity.clicks_all ? (theTotalEntity.leads / theTotalEntity.clicks_all) * 100 : 0
        },
    },
    {
        "key": "lead_form_opens",
        "name": "Lead form opens",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": false,
        "description": "The number of times users opened the LinkedIn lead gen form.",
        "formula": "Sum of lead form opens",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            linkedin_ads: (theInsightEntity) => theInsightEntity['oneClickLeadFormOpens'] ? Number(theInsightEntity['oneClickLeadFormOpens']) : 0,
        },
        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.lead_form_opens ? theTotalEntity.lead_form_opens + theCurrentEntity.lead_form_opens : theCurrentEntity.lead_form_opens
        },
    },
    {
        "key": "lead_form_completion_rate",
        "name": "Lead form completion rate",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": false,
        "percentages": true,
        "description": "The percentage of opened lead forms that were submitted.",
        "formula": "(Leads / Lead form opens) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["leads", "lead_form_opens"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.leads && theTotalEntity.lead_form_opens ? (theTotalEntity.leads / theTotalEntity.lead_form_opens) * 100 : 0
        },
    },
    {
        "key": "work_email_leads",
        "name": "Leads (work email)",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": false,
        "description": "Leads submitted with a valid work email (non-free/personal domain).",
        "formula": "Sum of work email leads",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            linkedin_ads: (theInsightEntity) => theInsightEntity['validWorkEmailLeads'] ? Number(theInsightEntity['validWorkEmailLeads']) : 0,
        },
        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.work_email_leads ? theTotalEntity.work_email_leads + theCurrentEntity.work_email_leads : theCurrentEntity.work_email_leads
        },
    },
    {
        "key": "cost_per_work_email_lead",
        "name": "Cost per lead (work email)",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": true,
        "percentages": false,
        "description": "Average cost per lead submitted with a valid work email.",
        "formula": "Total Spend / Work email leads",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["spend", "work_email_leads"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.spend && theTotalEntity.work_email_leads ? theTotalEntity.spend / theTotalEntity.work_email_leads : 0
        },
    },
    {
        "key": "average_leads_value",
        "name": "Average leads value",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": true,
        "percentages": false,
        "description": "The average monetary value assigned to each generated lead.",
        "formula": "Total value of leads / Number of leads",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        "dependsOn": ["leads", "leads_value"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.leads && theTotalEntity.leads_value ? theTotalEntity.leads_value / theTotalEntity.leads : 0
        }
    },
    {
        "key": "leads_roas",
        "name": "Leads ROAS",
        "color": "bg-gradient-to-r from-indigo-500 to-purple-500",
        "fromColor": "#6366F1",
        "toColor": "#A855F7",
        "currency": false,
        "percentages": true,
        "description": "Return on ad spend for lead generation asset, expressed as a percentage.",
        "formula": "(Total value of leads / Total ad spend)",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        "dependsOn": ["spend", "leads_value"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.spend && theTotalEntity.leads_value ? (theTotalEntity.leads_value / theTotalEntity.spend) : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['leads_roas'] ? Number(theInsightEntity['leads_roas']) : 0
        },
    },
    {
        "key": "offsite_search_add_meta_leads",
        "name": "Offsite Meta leads",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": false,
        "description": "Total number of Meta leads. (Offsite search add meta leads)",
        "formula": "Sum of Leads",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'offsite_search_add_meta_leads', 'actions', attributionWindowKeys)
            },
            // tiktok_ads: (theInsightEntity) => theInsightEntity['form'] ? Number(theInsightEntity['form']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.offsite_search_add_meta_leads ? theTotalEntity.offsite_search_add_meta_leads + theCurrentEntity.offsite_search_add_meta_leads : theCurrentEntity.offsite_search_add_meta_leads
        },
    },
    {
        "key": "subscriptions",
        "name": "Subscriptions",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": false,
        "percentages": false,
        "description": "Total number of subscriptions.",
        "formula": "Sum of Subscriptions",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'subscribe_total', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['subscriptions'] ? Number(theInsightEntity['subscriptions']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.subscriptions ? theTotalEntity.subscriptions + theCurrentEntity.subscriptions : theCurrentEntity.subscriptions
        },
    },
    {
        "key": "subscriptions_value",
        "name": "Subscriptions value",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": true,
        "percentages": false,
        "description": "Total value from subscriptions.",
        "formula": "Sum of Subscription Values",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'subscribe_total', 'action_values', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['subscriptions_value'] ? Number(theInsightEntity['subscriptions_value']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.subscriptions_value ? theTotalEntity.subscriptions_value + theCurrentEntity.subscriptions_value : theCurrentEntity.subscriptions_value
        },
    },
    {
        "key": "click_to_subscriptions",
        "name": "Click to subscriptions",
        "color": "bg-gradient-to-r from-pink-500 to-yellow-500",
        "fromColor": "#EC4899",
        "toColor": "#EAB308",
        "currency": false,
        "percentages": true,
        "description": "The percentage of clicks that result in new subscriptions.",
        "formula": "(Subscription events / Total clicks) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["subscriptions", "clicks_all"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.subscriptions && theTotalEntity.clicks_all ? (theTotalEntity.subscriptions / theTotalEntity.clicks_all) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['click_to_subscriptions'] ? Number(theInsightEntity['click_to_subscriptions']) : 0
        },
    },
    {
        "key": "cost_per_subscription",
        "name": "Cost per subscription",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": true,
        "percentages": false,
        "description": "Average cost per subscription.",
        "formula": "Total Spend / Subscriptions",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'subscribe_total', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_subscription'] ? Number(theInsightEntity['cost_per_subscription']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_subscription ? theTotalEntity.cost_per_subscription + theCurrentEntity.cost_per_subscription : theCurrentEntity.cost_per_subscription
        },
    },
    {
        "key": "leads_to_subscriptions_ratio",
        "name": "Leads to subscriptions ratio",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": false,
        "percentages": true,
        "description": "The percentage of leads that convert into subscriptions.",
        "formula": "(Subscription events / Total leads) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        "dependsOn": ["subscriptions", "leads"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.subscriptions && theTotalEntity.leads ? (theTotalEntity.subscriptions / theTotalEntity.leads) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['leads_to_subscriptions_ratio'] ? Number(theInsightEntity['leads_to_subscriptions_ratio']) : 0
        },
    },
    {
        "key": "registrations_completed",
        "name": "Registrations completed",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": false,
        "percentages": false,
        "description": "Total completed registrations.",
        "formula": "Sum of Completed Registrations",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'omni_complete_registration', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['registrations_completed'] ? Number(theInsightEntity['registrations_completed']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.registrations_completed ? theTotalEntity.registrations_completed + theCurrentEntity.registrations_completed : theCurrentEntity.registrations_completed
        },
    },
    {
        "key": "registrations_completed_value",
        "name": "Registrations completed value",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": true,
        "percentages": false,
        "description": "Total value from completed registrations.",
        "formula": "Sum of Values from Completed Registrations",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'omni_complete_registration', 'action_values', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['registrations_completed_value'] ? Number(theInsightEntity['registrations_completed_value']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.registrations_completed_value ? theTotalEntity.registrations_completed_value + theCurrentEntity.registrations_completed_value : theCurrentEntity.registrations_completed_value
        },
    },
    {
        "key": "cost_per_registration_completed",
        "name": "Cost per registration completed",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": true,
        "percentages": false,
        "description": "Average cost per completed registration.",
        "formula": "Total Spend / Completed Registrations",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'omni_complete_registration', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_registration_completed'] ? Number(theInsightEntity['cost_per_registration_completed']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_registration_completed ? theTotalEntity.cost_per_registration_completed + theCurrentEntity.cost_per_registration_completed : theCurrentEntity.cost_per_registration_completed
        },
    },
    {
        "key": "click_to_registrations_completed",
        "name": "Click to registrations completed",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": true,
        "description": "The percentage of clicks that result in completed registrations.",
        "formula": "(Registration completion events / Total clicks) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["registrations_completed", "clicks_all"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.registrations_completed && theTotalEntity.clicks_all ? (theTotalEntity.registrations_completed / theTotalEntity.clicks_all) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['click_to_registrations_completed'] ? Number(theInsightEntity['click_to_registrations_completed']) : 0
        },
    },
    {
        "key": "percent_install_to_registration",
        "name": "% install to registration",
        "color": "bg-gradient-to-r from-teal-400 to-green-500",
        "fromColor": "#2DD4BF",
        "toColor": "#22C55E",
        "currency": false,
        "percentages": true,
        "description": "The percentage of app installations that result in user registrations.",
        "formula": "(Registration events / Total app installs) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["app_installs", "registrations_completed"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.app_installs && theTotalEntity.registrations_completed ? (theTotalEntity.registrations_completed / theTotalEntity.app_installs) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['percent_install_to_registration'] ? Number(theInsightEntity['percent_install_to_registration']) : 0
        },
    },
    {
        "key": "add_payment_info_count_mobile",
        "name": "Add payment info count (mobile)",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": false,
        "description": "Total mobile add payment info events.",
        "formula": "Sum of Mobile Add Payment Info Events",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'app_custom_event.fb_mobile_add_payment_info', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['add_payment_info_count_mobile'] ? Number(theInsightEntity['add_payment_info_count_mobile']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.add_payment_info_count_mobile ? theTotalEntity.add_payment_info_count_mobile + theCurrentEntity.add_payment_info_count_mobile : theCurrentEntity.add_payment_info_count_mobile
        },
    },
    {
        "key": "add_payment_info_value_mobile",
        "name": "Add payment info value (mobile)",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": true,
        "percentages": false,
        "description": "Total value from mobile add payment info events.",
        "formula": "Sum of Values from Mobile Add Payment Info",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'app_custom_event.fb_mobile_add_payment_info', 'action_values', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['add_payment_info_value_mobile'] ? Number(theInsightEntity['add_payment_info_value_mobile']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.add_payment_info_value_mobile ? theTotalEntity.add_payment_info_value_mobile + theCurrentEntity.add_payment_info_value_mobile : theCurrentEntity.add_payment_info_value_mobile
        },
    },
    {
        "key": "cost_per_add_payment_info_mobile",
        "name": "Cost per add payment info (mobile)",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": true,
        "percentages": false,
        "description": "Average cost per mobile add payment info event.",
        "formula": "Total Spend / Mobile Add Payment Info Events",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'app_custom_event.fb_mobile_add_payment_info', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_add_payment_info_mobile'] ? Number(theInsightEntity['cost_per_add_payment_info_mobile']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_add_payment_info_mobile ? theTotalEntity.cost_per_add_payment_info_mobile + theCurrentEntity.cost_per_add_payment_info_mobile : theCurrentEntity.cost_per_add_payment_info_mobile
        },
    },
    {
        "key": "add_payment_info_count_website",
        "name": "Add payment info count (website)",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": false,
        "description": "Total website add payment info events.",
        "formula": "Sum of Website Add Payment Info Events",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'offsite_conversion.fb_pixel_add_payment_info', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['add_payment_info_count_website'] ? Number(theInsightEntity['add_payment_info_count_website']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.add_payment_info_count_website ? theTotalEntity.add_payment_info_count_website + theCurrentEntity.add_payment_info_count_website : theCurrentEntity.add_payment_info_count_website
        },
    },
    {
        "key": "add_payment_info_value_website",
        "name": "Add payment info value (website)",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": true,
        "percentages": false,
        "description": "Total value from website add payment info events.",
        "formula": "Sum of Values from Website Add Payment Info",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'offsite_conversion.fb_pixel_add_payment_info', 'action_values', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['add_payment_info_value_website'] ? Number(theInsightEntity['add_payment_info_value_website']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.add_payment_info_value_website ? theTotalEntity.add_payment_info_value_website + theCurrentEntity.add_payment_info_value_website : theCurrentEntity.add_payment_info_value_website
        },
    },
    {
        "key": "cost_per_add_payment_info_website",
        "name": "Cost per add payment info (website)",
        "color": "bg-gradient-to-r from-pink-500 to-yellow-500",
        "fromColor": "#EC4899",
        "toColor": "#EAB308",
        "currency": true,
        "percentages": false,
        "description": "Average cost per website add payment info event.",
        "formula": "Total Spend / Website Add Payment Info Events",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'offsite_conversion.fb_pixel_add_payment_info', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_add_payment_info_website'] ? Number(theInsightEntity['cost_per_add_payment_info_website']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_add_payment_info_website ? theTotalEntity.cost_per_add_payment_info_website + theCurrentEntity.cost_per_add_payment_info_website : theCurrentEntity.cost_per_add_payment_info_website
        },
    },
    {
        "key": "trials_started_total",
        "name": "Trials started (total)",
        "color": "bg-gradient-to-r from-indigo-500 to-purple-500",
        "fromColor": "#6366F1",
        "toColor": "#A855F7",
        "currency": false,
        "percentages": false,
        "description": "Total number of trials started.",
        "formula": "Sum of Trials Started",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'start_trial_total', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['trials_started_total'] ? Number(theInsightEntity['trials_started_total']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.trials_started_total ? theTotalEntity.trials_started_total + theCurrentEntity.trials_started_total : theCurrentEntity.trials_started_total
        },
    },
    {
        "key": "trials_started_value_total",
        "name": "Trials started value (total)",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": true,
        "percentages": false,
        "description": "Total value from all trials started.",
        "formula": "Sum of Values from Trials Started",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'start_trial_total', 'action_values', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['trials_started_value_total'] ? Number(theInsightEntity['trials_started_value_total']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.trials_started_value_total ? theTotalEntity.trials_started_value_total + theCurrentEntity.trials_started_value_total : theCurrentEntity.trials_started_value_total
        },
    },
    {
        "key": "cost_per_trial_started_total",
        "name": "Cost per trial started (total)",
        "color": "bg-gradient-to-r from-indigo-500 to-purple-500",
        "fromColor": "#6366F1",
        "toColor": "#A855F7",
        "currency": true,
        "percentages": false,
        "description": "Average cost per trial started.",
        "formula": "Total Spend / Trials Started",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'start_trial_total', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_trial_started_total'] ? Number(theInsightEntity['cost_per_trial_started_total']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_trial_started_total ? theTotalEntity.cost_per_trial_started_total + theCurrentEntity.cost_per_trial_started_total : theCurrentEntity.cost_per_trial_started_total
        },
    },
    {
        "key": "trials_started_mobile",
        "name": "Trials started (mobile)",
        "color": "bg-gradient-to-r from-pink-500 to-yellow-500",
        "fromColor": "#EC4899",
        "toColor": "#EAB308",
        "currency": false,
        "percentages": false,
        "description": "Total trials started on mobile.",
        "formula": "Sum of Mobile Trials Started",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'start_trial_mobile_app', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['trials_started_mobile'] ? Number(theInsightEntity['trials_started_mobile']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.trials_started_mobile ? theTotalEntity.trials_started_mobile + theCurrentEntity.trials_started_mobile : theCurrentEntity.trials_started_mobile
        },
    },
    {
        "key": "trials_started_value_mobile",
        "name": "Trials started value (mobile)",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": true,
        "percentages": false,
        "description": "Total value from mobile trials started.",
        "formula": "Sum of Values from Mobile Trials Started",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'start_trial_mobile_app', 'action_values', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['trials_started_value_mobile'] ? Number(theInsightEntity['trials_started_value_mobile']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.trials_started_value_mobile ? theTotalEntity.trials_started_value_mobile + theCurrentEntity.trials_started_value_mobile : theCurrentEntity.trials_started_value_mobile
        },
    },
    {
        "key": "cost_per_trial_started_mobile",
        "name": "Cost per trial started (mobile)",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": true,
        "percentages": false,
        "description": "Average cost per mobile trial started.",
        "formula": "Total Spend / Mobile Trials Started",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'start_trial_mobile_app', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_trial_started_mobile'] ? Number(theInsightEntity['cost_per_trial_started_mobile']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_trial_started_mobile ? theTotalEntity.cost_per_trial_started_mobile + theCurrentEntity.cost_per_trial_started_mobile : theCurrentEntity.cost_per_trial_started_mobile
        },
    },
    {
        "key": "trials_started_website",
        "name": "Trials started (website)",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": false,
        "percentages": false,
        "description": "Total trials started on the website.",
        "formula": "Sum of Website Trials Started",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'start_trial_website', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['trials_started_website'] ? Number(theInsightEntity['trials_started_website']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.trials_started_website ? theTotalEntity.trials_started_website + theCurrentEntity.trials_started_website : theCurrentEntity.trials_started_website
        },
    },
    {
        "key": "trials_started_value_website",
        "name": "Trials started value (website)",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": true,
        "percentages": false,
        "description": "Total value from website trials started.",
        "formula": "Sum of Values from Website Trials Started",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'start_trial_website', 'action_values', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['trials_started_value_website'] ? Number(theInsightEntity['trials_started_value_website']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.trials_started_value_website ? theTotalEntity.trials_started_value_website + theCurrentEntity.trials_started_value_website : theCurrentEntity.trials_started_value_website
        },
    },
    {
        "key": "cost_per_trial_started_website",
        "name": "Cost per trial started (website)",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": true,
        "percentages": false,
        "description": "Average cost per website trial started.",
        "formula": "Total Spend / Website Trials Started",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'start_trial_website', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_trial_started_website'] ? Number(theInsightEntity['cost_per_trial_started_website']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_trial_started_website ? theTotalEntity.cost_per_trial_started_website + theCurrentEntity.cost_per_trial_started_website : theCurrentEntity.cost_per_trial_started_website
        },
    },
    {
        "key": "percent_install_to_trial",
        "name": "% install to trial",
        "color": "bg-gradient-to-r from-pink-500 to-yellow-500",
        "fromColor": "#EC4899",
        "toColor": "#EAB308",
        "currency": false,
        "percentages": true,
        "description": "The percentage of app installations that convert into trial starts.",
        "formula": "(Trial start events / Total app installs) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["app_installs", "trials_started_total"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.app_installs && theTotalEntity.trials_started_total ? (theTotalEntity.trials_started_total / theTotalEntity.app_installs) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['percent_install_to_trial'] ? Number(theInsightEntity['percent_install_to_trial']) : 0
        },
    },
    {
        "key": "click_to_trial_started",
        "name": "Click to trial started",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": false,
        "percentages": true,
        "description": "The percentage of clicks that result in trial starts.",
        "formula": "(Trial start events / Total clicks) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        "dependsOn": ["trials_started_total", "clicks_all"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.trials_started_total && theTotalEntity.clicks_all ? (theTotalEntity.trials_started_total / theTotalEntity.clicks_all) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['click_to_trial_started'] ? Number(theInsightEntity['click_to_trial_started']) : 0
        },
    },
    {
        "key": "trial_started_total_skan_and_non_skan",
        "name": "Trial started (total) (SKAN + non-SKAN)",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": false,
        "percentages": false,
        "description": "Total number of trial starts, combining SKAN and non-SKAN data.",
        "formula": "SKAN Trial Starts + Non-SKAN Trial Starts",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'start_trial_total', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['trial_started_total_skan_and_non_skan'] ? Number(theInsightEntity['trial_started_total_skan_and_non_skan']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.trial_started_total_skan_and_non_skan ? theTotalEntity.trial_started_total_skan_and_non_skan + theCurrentEntity.trial_started_total_skan_and_non_skan : theCurrentEntity.trial_started_total_skan_and_non_skan
        },
    },
    {
        "key": "cost_per_trial_started_total_skan_and_non_skan",
        "name": "Cost per trial started (SKAN + non-SKAN)",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": true,
        "percentages": false,
        "description": "Average cost to acquire a trial start, including both SKAN and non-SKAN data.",
        "formula": "Total Spend / Total Trial Starts (SKAN + Non-SKAN)",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                // How is that different from the 'cost_per_trial_started_total'?
                return extractActionsPropertiesByType(theInsightEntity, 'start_trial_total', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_trial_started_total_skan_and_non_skan'] ? Number(theInsightEntity['cost_per_trial_started_total_skan_and_non_skan']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_trial_started_total_skan_and_non_skan ? theTotalEntity.cost_per_trial_started_total_skan_and_non_skan + theCurrentEntity.cost_per_trial_started_total_skan_and_non_skan : theCurrentEntity.cost_per_trial_started_total_skan_and_non_skan
        },
    },
    {
        "key": "value_of_products_customized",
        "name": "Value of products customized",
        "color": "bg-gradient-to-r from-indigo-500 to-purple-500",
        "fromColor": "#6366F1",
        "toColor": "#A855F7",
        "currency": true,
        "percentages": false,
        "description": "Total value of customized products.",
        "formula": "Sum of Values from Products Customized",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'customize_product_total', 'action_values', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['value_of_products_customized'] ? Number(theInsightEntity['value_of_products_customized']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.value_of_products_customized ? theTotalEntity.value_of_products_customized + theCurrentEntity.value_of_products_customized : theCurrentEntity.value_of_products_customized
        },
    },
    {
        "key": "cost_per_product_customized",
        "name": "Cost per product customized",
        "color": "bg-gradient-to-r from-teal-400 to-green-500",
        "fromColor": "#2DD4BF",
        "toColor": "#22C55E",
        "currency": true,
        "percentages": false,
        "description": "Average cost per customized product.",
        "formula": "Total Spend / Products Customized",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'customize_product_total', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_product_customized'] ? Number(theInsightEntity['cost_per_product_customized']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_product_customized ? theTotalEntity.cost_per_product_customized + theCurrentEntity.cost_per_product_customized : theCurrentEntity.cost_per_product_customized
        },
    },
    {
        "key": "submit_application",
        "name": "Submit application",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": false,
        "percentages": false,
        "description": "Total number of submitted applications.",
        "formula": "Sum of Applications Submitted",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'submit_application_total', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['submit_application'] ? Number(theInsightEntity['submit_application']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.submit_application ? theTotalEntity.submit_application + theCurrentEntity.submit_application : theCurrentEntity.submit_application
        },
    },
    {
        "key": "submit_application_value",
        "name": "Submit application value",
        "color": "bg-gradient-to-r from-indigo-500 to-purple-500",
        "fromColor": "#6366F1",
        "toColor": "#A855F7",
        "currency": true,
        "percentages": false,
        "description": "Total value from submitted applications.",
        "formula": "Sum of Values from Applications Submitted",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'submit_application_total', 'action_values', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['submit_application_value'] ? Number(theInsightEntity['submit_application_value']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.submit_application_value ? theTotalEntity.submit_application_value + theCurrentEntity.submit_application_value : theCurrentEntity.submit_application_value
        },
    },
    {
        "key": "cost_per_submit_application",
        "name": "Cost per submit application",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": true,
        "percentages": false,
        "description": "Average cost per submitted application.",
        "formula": "Spend / Applications Submitted",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'submit_application_total', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_submit_application'] ? Number(theInsightEntity['cost_per_submit_application']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_submit_application ? theTotalEntity.cost_per_submit_application + theCurrentEntity.cost_per_submit_application : theCurrentEntity.cost_per_submit_application
        },
    },
    {
        "key": "percent_click_to_submit_application",
        "name": "% click to submit application",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": true,
        "description": "Percentage of clicks that lead to an application submission.",
        "formula": "(Applications / Clicks) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        "dependsOn": ["clicks_all", "submit_application"],
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['percent_click_to_submit_application'] ? Number(theInsightEntity['percent_click_to_submit_application']) : 0
        },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.clicks_all && theTotalEntity.submit_application ? (theTotalEntity.submit_application / theTotalEntity.clicks_all) * 100 : 0
        },
    },
    {
        "key": "total_contacts",
        "name": "Total contacts",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": false,
        "percentages": false,
        "description": "Total number of contacts made.",
        "formula": "Sum of Contacts",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'contact_total', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['total_contacts'] ? Number(theInsightEntity['total_contacts']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.total_contacts ? theTotalEntity.total_contacts + theCurrentEntity.total_contacts : theCurrentEntity.total_contacts
        },
    },
    {
        "key": "contact_value",
        "name": "Contact value",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": true,
        "percentages": false,
        "description": "Total value from contacts.",
        "formula": "Sum of Contact Values",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'contact_total', 'action_values', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['contact_value'] ? Number(theInsightEntity['contact_value']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.contact_value ? theTotalEntity.contact_value + theCurrentEntity.contact_value : theCurrentEntity.contact_value
        },
    },
    {
        "key": "cost_per_contact",
        "name": "Cost per contact",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "currency": true,
        "percentages": false,
        "description": "Average cost per contact.",
        "formula": "Total Spend / Contacts",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'contact_total', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_contact'] ? Number(theInsightEntity['cost_per_contact']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_contact ? theTotalEntity.cost_per_contact + theCurrentEntity.cost_per_contact : theCurrentEntity.cost_per_contact
        },
    },
    {
        "key": "click_to_contact_ratio",
        "name": "Click to contact ratio",
        "color": "bg-gradient-to-r from-teal-400 to-green-500",
        "fromColor": "#2DD4BF",
        "toColor": "#22C55E",
        "currency": false,
        "percentages": true,
        "description": "The percentage of clicks that result in contact form submissions or inquiries.",
        "formula": "(Total Contact events / Total clicks) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        "dependsOn": ["total_contacts", "clicks_all"],
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['click_to_contact_ratio'] ? Number(theInsightEntity['click_to_contact_ratio']) : 0
        },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.total_contacts && theTotalEntity.clicks_all ? (theTotalEntity.total_contacts / theTotalEntity.clicks_all) * 100 : 0
        },
    },
    {
        "key": "total_find_locations",
        "name": "Total find locations",
        "color": "bg-gradient-to-r from-pink-500 to-yellow-500",
        "fromColor": "#EC4899",
        "toColor": "#EAB308",
        "currency": false,
        "percentages": false,
        "description": "Total number of 'find location' events.",
        "formula": "Sum of Find Location Events",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'find_location_total', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['total_find_locations'] ? Number(theInsightEntity['total_find_locations']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.total_find_locations ? theTotalEntity.total_find_locations + theCurrentEntity.total_find_locations : theCurrentEntity.total_find_locations
        },
    },
    {
        "key": "find_location_value",
        "name": "Find location value",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": true,
        "percentages": false,
        "description": "Total value from 'find location' events.",
        "formula": "Sum of Values from Find Location Events",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'find_location_total', 'action_values', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['find_location_value'] ? Number(theInsightEntity['find_location_value']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.find_location_value ? theTotalEntity.find_location_value + theCurrentEntity.find_location_value : theCurrentEntity.find_location_value
        },
    },
    {
        "key": "cost_per_find_location",
        "name": "Cost per find location",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": true,
        "percentages": false,
        "description": "Average cost per 'find location' event.",
        "formula": "Total Spend / Find Location Events",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'find_location_total', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_find_location'] ? Number(theInsightEntity['cost_per_find_location']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_find_location ? theTotalEntity.cost_per_find_location + theCurrentEntity.cost_per_find_location : theCurrentEntity.cost_per_find_location
        },
    },
    {
        "key": "click_to_find_location_ratio",
        "name": "Click to find location ratio",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": false,
        "percentages": true,
        "description": "The percentage of clicks that lead to location finder usage.",
        "formula": "(Total Location find events / Total clicks) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        "dependsOn": ["total_find_locations", "clicks_all"],
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['click_to_find_location_ratio'] ? Number(theInsightEntity['click_to_find_location_ratio']) : 0
        },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.total_find_locations && theTotalEntity.clicks_all ? (theTotalEntity.total_find_locations / theTotalEntity.clicks_all) * 100 : 0
        },
    },
    {
        // Maybe it's inside the 'actions' object?
        "key": "add_to_wishlist",
        "name": "Add to Wishlist",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": false,
        "description": "Total number of items added to the wishlist.",
        "formula": "Sum of Add to Wishlist Events",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'offsite_conversion.fb_pixel_add_to_wishlist', 'actions', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['add_to_wishlist'] ? Number(theInsightEntity['add_to_wishlist']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.add_to_wishlist ? theTotalEntity.add_to_wishlist + theCurrentEntity.add_to_wishlist : theCurrentEntity.add_to_wishlist
        },
    },
    {
        "key": "add_to_wishlist_value",
        "name": "Add to Wishlist Value",
        "color": "bg-gradient-to-r from-indigo-500 to-purple-500",
        "fromColor": "#6366F1",
        "toColor": "#A855F7",
        "currency": true,
        "percentages": false,
        "description": "Total value of items added to the wishlist.",
        "formula": "Sum of Values from Add to Wishlist Events",
        "bestValue": "high",
        "displaySum": true,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'offsite_conversion.fb_pixel_add_to_wishlist', 'action_values', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['add_to_wishlist_value'] ? Number(theInsightEntity['add_to_wishlist_value']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.add_to_wishlist_value ? theTotalEntity.add_to_wishlist_value + theCurrentEntity.add_to_wishlist_value : theCurrentEntity.add_to_wishlist_value
        },
    },
    {
        "key": "cost_per_add_to_wishlist",
        "name": "Cost per add to wishlist",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": true,
        "percentages": false,
        "description": "Average cost per 'Add to Wishlist' event.",
        "formula": "Total Spend / Add to Wishlist Events",
        "bestValue": "low",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        extractors: {
            meta: (theInsightEntity, attributionWindowKeys) => {
                return extractActionsPropertiesByType(theInsightEntity, 'offsite_conversion.fb_pixel_add_to_wishlist', 'cost_per_action_type', attributionWindowKeys)
            },
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_add_to_wishlist'] ? Number(theInsightEntity['cost_per_add_to_wishlist']) : 0
        },

        calculationFunction: (theTotalEntity, theCurrentEntity) => {
            return theTotalEntity.cost_per_add_to_wishlist ? theTotalEntity.cost_per_add_to_wishlist + theCurrentEntity.cost_per_add_to_wishlist : theCurrentEntity.cost_per_add_to_wishlist
        },
    },
    {
        "key": "percent_install_to_purchase",
        "name": "% install to purchase",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": true,
        "description": "The percentage of app installations that convert into purchases.",
        "formula": "(Purchase events / Total app installs) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["app_installs", "purchases"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.app_installs && theTotalEntity.purchases ? (theTotalEntity.purchases / theTotalEntity.app_installs) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['percent_install_to_purchase'] ? Number(theInsightEntity['percent_install_to_purchase']) : 0
        },
    },
    {
        "key": "percent_registration_to_achievement_unlocked",
        "name": "% registration to achievement unlocked",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": false,
        "percentages": true,
        "description": "The percentage of registered users who unlock an achievement in the app.",
        "formula": "(Achievement unlock events / Total registrations) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        "dependsOn": ["registrations_completed", "achievement_unlocked"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.registrations_completed && theTotalEntity.achievement_unlocked ? (theTotalEntity.achievement_unlocked / theTotalEntity.registrations_completed) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['percent_registration_to_achievement_unlocked'] ? Number(theInsightEntity['percent_registration_to_achievement_unlocked']) : 0
        },
    },
    {
        "key": "percent_achievement_unlocked_to_purchase",
        "name": "% achievement unlocked to purchase",
        "color": "bg-gradient-to-r from-teal-400 to-green-500",
        "fromColor": "#2DD4BF",
        "toColor": "#22C55E",
        "currency": false,
        "percentages": true,
        "description": "Percentage of users who unlocked an achievement and then made a purchase.",
        "formula": "(Purchases from Achievement Unlocks / Total Achievement Unlocks) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "dependsOn": ["achievement_unlocked", "purchases"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.achievement_unlocked && theTotalEntity.purchases ? (theTotalEntity.purchases / theTotalEntity.achievement_unlocked) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['percent_achievement_unlocked_to_purchase'] ? Number(theInsightEntity['percent_achievement_unlocked_to_purchase']) : 0
        },
    },
    {
        "key": "percent_clicks_to_initiate_checkout",
        "name": "% clicks to initiate checkout",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": true,
        "description": "The percentage of clicks that result in users starting the checkout process.",
        "formula": "(Checkout initiation events / Total clicks) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        "dependsOn": ["ic", "clicks_all"],
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.ic && theTotalEntity.clicks_all ? (theTotalEntity.ic / theTotalEntity.clicks_all) * 100 : 0
        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['percent_clicks_to_initiate_checkout'] ? Number(theInsightEntity['percent_clicks_to_initiate_checkout']) : 0
        },
    },
    // ------------------------------------------------
    // Not sure || Need to double verify || Wrong values when I calculate
    // ------------------------------------------------
    {
        "key": "percent_purchase_value",
        "name": "% purchase value",
        "color": "bg-gradient-to-r from-green-400 to-blue-500",
        "fromColor": "#4ADE80",
        "toColor": "#3B82F6",
        "currency": false,
        "percentages": true,
        "description": "Percentage of purchase revenue generated by an ad out of total revenue",
        "formula": "(Purchase Revenue / Total Revenue) * 100",
        "bestValue": "high",
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['percent_purchase_value'] ? Number(theInsightEntity['percent_purchase_value']) : 0
        },
    },
    {
        // Probably need to sum all the 'actions' which starts with 'offsite_conversion.'
        "key": "offline_conversion_count",
        "name": "Offline conversion count",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": false,
        "percentages": false,
        "description": "Total number of offline conversions.",
        "formula": "Sum of Offline Conversions",
        "bestValue": "low",
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['offline_conversion_count'] ? Number(theInsightEntity['offline_conversion_count']) : 0
        },
    },
    {
        // Probably need to sum all the 'action_values' which starts with 'offsite_conversion.'
        "key": "offline_conversion_value",
        "name": "Offline conversion value",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "currency": true,
        "percentages": false,
        "description": "Total value generated from offline conversions.",
        "formula": "Sum of Offline Conversion Values",
        "bestValue": "high",
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['offline_conversion_value'] ? Number(theInsightEntity['offline_conversion_value']) : 0
        },
    },
    {
        // Probably need to sum all the 'cost_per_action' which starts with 'offsite_conversion.'
        "key": "cost_per_offline_conversion",
        "name": "Cost per offline conversion",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": true,
        "percentages": false,
        "description": "Average cost per offline conversion.",
        "formula": "Total Spend / Offline Conversions",
        "bestValue": "low",
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_offline_conversion'] ? Number(theInsightEntity['cost_per_offline_conversion']) : 0
        },
    },
    {
        // Need to check if i have 'offline_purchase' inside the 'actions' array (?)
        "key": "offline_purchase_count",
        "name": "Offline purchase count",
        "color": "bg-gradient-to-r from-indigo-500 to-purple-500",
        "fromColor": "#6366F1",
        "toColor": "#A855F7",
        "currency": false,
        "percentages": false,
        "description": "Total number of offline purchases.",
        "formula": "Sum of Offline Purchases",
        "bestValue": "high",
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['offline_purchase_count'] ? Number(theInsightEntity['offline_purchase_count']) : 0
        },
    },
    {
        // Need to check if i have 'offline_purchase' inside the 'action_values' array (?)
        "key": "offline_purchase_value",
        "name": "Offline purchase value",
        "color": "bg-gradient-to-r from-teal-400 to-green-500",
        "fromColor": "#2DD4BF",
        "toColor": "#22C55E",
        "currency": true,
        "percentages": false,
        "description": "Total value generated from offline purchases.",
        "formula": "Sum of Offline Purchase Values",
        "bestValue": "high",
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['offline_purchase_value'] ? Number(theInsightEntity['offline_purchase_value']) : 0
        },
    },
    {
        // Need to check if i have 'offline_purchase' inside the 'cost_per_action' array (?)
        "key": "cost_per_offline_purchase",
        "name": "Cost per offline purchase",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": true,
        "percentages": false,
        "description": "Average cost per offline purchase.",
        "formula": "Total Spend / Offline Purchases",
        "bestValue": "low",
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['cost_per_offline_purchase'] ? Number(theInsightEntity['cost_per_offline_purchase']) : 0
        },
    },
    {
        "key": "view_content_ratio",
        "name": "View content ratio",
        "color": "bg-gradient-to-r from-purple-500 to-pink-500",
        "fromColor": "#A855F7",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": true,
        "description": "Ratio Of all outbound clicks triggered, the percent of times a user triggered a view content event",
        "formula": "(View Content / Outbound Clicks) * 100",
        "bestValue": "high",
        "displaySum": false,
        "displayAverage": true,
        "needVerify": true,
        "dependsOn": ["view_content", "clicks_outbound"],
        // calculationFunction: (theTotalEntity) => {
        // total.view_content_ratio = (total.view_content && total.clicks_outbound) ? ((total.view_content / total.clicks_outbound) * 100) : 0 // This is not good, not sure why
        //     return theTotalEntity.view_content && theTotalEntity.clicks_outbound ? (theTotalEntity.view_content / theTotalEntity.clicks_outbound) * 100 : 0
        // }
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['view_content_ratio'] ? Number(theInsightEntity['view_content_ratio']) : 0
        },
    },
    {
        // Not sure if it's 'estimated_ad_recall_rate_lower_bound' or 'estimated_ad_recall_rate_upper_bound' under the 'insight' entity
        "key": "estimated_ad_recall_lift_people",
        "name": "Estimated ad recall lift (people)",
        "color": "bg-gradient-to-r from-blue-500 to-teal-400",
        "fromColor": "#3B82F6",
        "toColor": "#2DD4BF",
        "currency": false,
        "percentages": false,
        "description": "Estimated number of people who recall seeing your ad.",
        "formula": "Ad Recall Estimate",
        "bestValue": "low",
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['estimated_ad_recall_lift_people'] ? Number(theInsightEntity['estimated_ad_recall_lift_people']) : 0
        },
    },
    {
        "key": "estimated_ad_recall_lift_rate",
        "name": "Estimated ad recall lift rate",
        "color": "bg-gradient-to-r from-red-500 to-pink-500",
        "fromColor": "#EF4444",
        "toColor": "#EC4899",
        "currency": false,
        "percentages": true,
        "description": "Estimated rate at which people recall seeing your ad.",
        "formula": "(Ad Recall Estimate / Impressions) * 100",
        "bestValue": "high",
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['estimated_ad_recall_lift_rate'] ? Number(theInsightEntity['estimated_ad_recall_lift_rate']) : 0
        },
    },
    {
        "key": "estimated_call_confirmations",
        "name": "Estimated call confirmations",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": false,
        "percentages": false,
        "description": "Estimated number of call confirmations.",
        "formula": "Sum of estimated call confirmations",
        "bestValue": "low",
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['estimated_call_confirmations'] ? Number(theInsightEntity['estimated_call_confirmations']) : 0
        },
    },
    /* Calculated from the benchmark */
    {
        "key": "spend_percentage",
        "name": "% Spend",
        "color": "bg-gradient-to-r from-yellow-400 to-orange-500",
        "fromColor": "#FACC15",
        "toColor": "#F97316",
        "currency": false,
        "percentages": true,
        "description": "The % of the total spend",
        "formula": "Current Spend / Total Spend",
        "bestValue": "high",
        "isNatural": true,
        "displaySum": false,
        "displayAverage": false,
        "dependsOn": ["spend"],
        "calculateFromBenchmark": true,
        calculateFromBenchmarkFunction: (benchmarkData, theTotalEntity) => {
            const totalSpend = benchmarkData.spend?.total
            const currentSpend = theTotalEntity.spend
            return totalSpend && currentSpend ? (currentSpend / totalSpend) * 100 : 0

        },
        extractors: {
            tiktok_ads: (theInsightEntity) => theInsightEntity['spend_percentage'] ? Number(theInsightEntity['spend_percentage']) : 0
        },
        calculationFunction: (theTotalEntity) => {
            return theTotalEntity.spend
        },
    },
]